import type {
  ChallanDirection,
  ChallanStatus,
  ChallanType,
  CreditNoteStatus,
  DocumentKind,
} from "@steelbill/types";

/**
 * The API surface, as the SPA sees it (spec §7).
 *
 * WHY THIS FILE EXISTS. Path strings scattered through twelve screen files are
 * how a rename becomes a hunt, and how one screen quietly calls
 * `/api/v1/customer` while the rest call `/customers`. Every path the app uses
 * is spelled once, here, relative to `VITE_API_BASE_URL` (§13 — no hostnames in
 * source; see `lib/env.ts`).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE RESPONSE ENVELOPES ARE NOT UNIFORM, AND THIS FILE DOES NOT PRETEND THEY
 * ARE. `/customers` answers `{ data, total, limit, offset }`, `/items` answers
 * `{ items, total, limit, offset }`, `/invoices` answers
 * `{ invoices, total, limit, offset }`. Wrapping that in a normalising client
 * would hide a real difference behind a lie that breaks the day someone reads
 * the API source. Each entry below states its envelope; read it before writing
 * the query.
 *
 * REQUEST BODIES ARE NEVER TYPED HERE. They come from the zod schemas in
 * `@steelbill/types` — `QuotationCreateSchema`, `InvoiceCreateSchema`,
 * `CustomerCreateSchema`, `ItemCreateSchema`, `OwnerLoginSchema` … — which are
 * the same objects the server validates with (§13). Redefining a request shape
 * in the SPA is how the client and server drift.
 *
 * ENUM UNIONS ARE IMPORTED, NOT RETYPED — but only where the union is
 * load-bearing. Most `status` fields below are plain `string`, because the screen
 * that reads them already has a translated-label map that fails loudly on an
 * unknown value. The challan types are the exception: `direction` decides which
 * party prints as consigner (Rule 55(1)), and re-spelling `"OUTWARD" | "INWARD"`
 * here would create a second definition that a later `ALTER TABLE` could leave
 * behind. `@steelbill/types` is the one place those live (§17.7).
 * ─────────────────────────────────────────────────────────────────────────────
 */

export const endpoints = {
  /* ---------------------------------------------------------------- auth */
  /** POST — body `OwnerLoginSchema`. Answers `AuthTokens`. Rate-limited hard. */
  ownerLogin: "/auth/owner-login",
  /** POST — body `AdminLoginSchema`. Answers `AuthTokens`. */
  adminLogin: "/auth/admin-login",
  /** POST — body `RefreshSchema`. Answers `AuthTokens`. Used only by `lib/api.ts`. */
  refresh: "/auth/refresh",
  /**
   * POST — no body, answers 204. THE ONLY AUTHENTICATED ROUTE UNDER `/auth`.
   *
   * It exists to write the audit row (§13, §14.3): the owner asked for "who
   * logged in when, what happened", and a window of sign-ins with no sign-outs
   * reads as though nobody ever leaves. It does NOT end the session on the
   * server — there is no token blacklist — so the client clears its own tokens
   * regardless of whether this call succeeds, and must never block sign-out on
   * it.
   */
  logout: "/auth/logout",

  /* ------------------------------------------------------------- profile */
  /*
   * ═══════════════════════════════════════════════════════════════════════════
   * §2, §2.1 — MY OWN ACCOUNT. `profile.manage`, which ALL THREE ROLES HOLD, and
   * that is the whole reason this prefix is not four more routes on `/users`:
   * every path there needs `user.manage`, so until this shipped the owner could
   * not change his own PIN — his son had to (apps/api/src/routes/profile.ts).
   *
   * NO PATH HERE TAKES AN `:id`, and that is a statement rather than an economy:
   * the target is always the caller's own row, so there is no request on this
   * prefix that can be aimed at somebody else's account.
   *
   * NOT BEHIND A FEATURE FLAG, deliberately (§18.3 forbids flagging
   * authentication). A 404 from any of these is a REAL 404 — an API older than
   * this bundle — never "the module is dark". Do not reach for `FeatureOff`.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  /**
   * GET → `{ data }` — my own row, read from the DATABASE rather than echoed
   * from the token, so a name or a door an administrator changed a minute ago is
   * the one that comes back.
   *
   * **PUT** — body `ProfileUpdateSchema` (`{ displayName }`, `.strict()`) →
   * `{ data, warnings? }`. NO CURRENT CREDENTIAL: a display name is what the
   * audit trail calls you, not something you sign in with, and a typo is
   * reversible by the person who made it. The two POSTs below are the ones that
   * can lock somebody out, and only those two ask.
   */
  profile: "/profile",
  /**
   * POST — body `ProfileCredentialSchema` (`{ currentPin | currentPassword }`
   * plus exactly one of `{ pin | password }`) → `{ data, warnings? }`.
   *
   * THREE REFUSALS THE SCREEN MUST NOT RE-DERIVE, ONLY RENDER:
   *   401 `That is not your current PIN or password.` — the CURRENT secret was
   *       wrong. Send it with `reauthenticating: true` (see `lib/api.ts`) or the
   *       client's refresh-and-retry treats a typo as an expired session and
   *       signs the person out of the app.
   *   403 a PIN may not rewrite a PASSWORD (`assertProofMayRewrite`). Password
   *       speaks for PIN, never the reverse — so never OFFER that crossing.
   *   409 that door is not open on this account. Opening one is an
   *       administrator's `POST /users/:id/doors`, never self-service.
   *
   * A SECRET GOES IN AND NEVER COMES BACK. `UserResponse` carries
   * `hasCredential`, a boolean ABOUT a hash. Never echo, cache or log one.
   */
  profileCredential: "/profile/credential",
  /**
   * POST — body `ProfileIdentifierSchema` (`{ currentPin | currentPassword }`
   * plus exactly one of `{ phone | email }`) → `{ data, warnings? }`.
   *
   * THE VALUE IT WRITES IS HALF OF WHAT THE PERSON TYPES TO SIGN IN, which is
   * why it asks for the current secret exactly as the credential route does, and
   * why its success `warnings` ("from now on sign in with …") is the one message
   * on this screen that must never be swallowed. A value already in use is a
   * 409; the message does not say whose, and neither should the screen.
   */
  profileIdentifier: "/profile/identifier",

  /* ----------------------------------------------------------- dashboard */
  /**
   * GET `?fy=` — the home screen (§11.2). Answers {@link DashboardSummaryResponse}
   * UNWRAPPED (no `data` key). Needs `report.view`.
   */
  dashboardSummary: "/dashboard/summary",

  /* ----------------------------------------------------------- customers */
  /** GET `?q&limit&offset` → `{ data, total, limit, offset }`. POST → `{ data, warnings }`. */
  customers: "/customers",
  /** GET → `{ data }`. PUT → `{ data, warnings }`. */
  customer: (id: number | string) => `/customers/${id}`,
  /**
   * **POST**, body `GstinCheckSchema` (`{ gstin }`) → `{ valid, stateCode,
   * stateName, supplyType }`, or `{ valid: false, reason }`.
   *
   * A POST rather than a GET because that is what the route is
   * (apps/api/src/routes/customers.ts), and it answers 200 with
   * `valid: false` for a bad GSTIN — "is this valid?" has "no" as a successful
   * answer. Never a rate lookup, and never a call to any government API (§10.1).
   */
  validateGstin: "/customers/validate-gstin",

  /* --------------------------------------------------------------- items */
  /** GET `?q&limit&offset` → `{ items, total, limit, offset }`. POST → `{ item }`. */
  items: "/items",
  /** PUT → `{ item }`. */
  item: (id: number | string) => `/items/${id}`,
  /**
   * POST — §4.1/§11.1's "save this custom line to my item list" prompt after a
   * save. Body `ItemFromLineSchema`. → `{ item }`.
   */
  itemFromLine: "/items/from-line",
  /** POST — ADMIN only (`item.adjust_stock`). → `{ item }`. */
  itemAdjustStock: (id: number | string) => `/items/${id}/adjust-stock`,

  /* ----------------------------------------------------------------- hsn */
  /**
   * GET `?q&limit` — the SAC/HSN picker.
   *
   * §8.6: there is NO rate lookup API and never will be. This searches the
   * static CA-verified seed. And §4.1: the words "HSN", "SAC", "supply nature"
   * and "works contract" never appear in front of the OWNER — the picker shows
   * a plain description and a correctable rate chip.
   */
  hsn: "/hsn",
  /** GET `?limit` — the codes actually used, most-used first. Pins the picker. */
  hsnFrequent: "/hsn/frequent",
  /**
   * POST — the §8.7 procedure, ADMIN only (`taxcode.write`). Body
   * `RateChangeSchema`. Adds a NEW rate period and supersedes the old one in one
   * transaction; a rate is never edited in place.
   */
  hsnRateChange: (code: string) => `/hsn/${code}/rate-change`,
  /**
   * PUT — §8.6's "last verified on / verified by" stamp, ADMIN only. Addressed
   * by rate-PERIOD id, not by code: a code has several periods and only one of
   * them was the one the CA looked at. Changes no rate.
   */
  hsnVerified: (id: number | string) => `/hsn/${id}/verified`,

  /* ---------------------------------------------------------- quotations */
  /**
   * GET `?q&status&customerId&from&to&sort&dir&limit&offset`
   * → `{ data, total, limit, offset }`.
   *
   * THAT LIST IS EXHAUSTIVE. `QuotationListQuerySchema` (`@steelbill/types`) is
   * `.strict()`, so an unknown key is a 400, not a shrug — note in particular
   * that there is no amount range here, though the bill register has one.
   *
   * `q` matches the quotation's OWN reference numbers, `quote_number` and
   * `po_ref_no`. It does NOT match the customer name — the buyer lives in
   * `customer_snapshot`, a text-backed JSON column. "Find Lupin's quotations" is
   * the `customerId` filter; `from`/`to` are inclusive calendar days on
   * `quote_date`.
   */
  quotations: "/quotations",
  /**
   * POST — body `QuotationCreateSchema`, the SAME schema the save validates.
   * → `{ data, warnings }` where `data` is a {@link DocumentPreview}.
   *
   * "Show me this before I save it." It writes nothing, consumes NO quotation
   * number (§6) and returns no `number` or `id` at all — the totals in it are
   * the server's, run through the same pipeline the save runs (§7, §8.7). Needs
   * `quotation.create`: previewing an unsaved document is part of authoring one.
   */
  quotationPreview: "/quotations/preview",
  /**
   * GET → `{ data }`. PUT → `{ data, warnings }`.
   *
   * THERE IS NO `DELETE` ON THIS PATH ANY MORE. §4 replaced it on 2026-08-13
   * with {@link quotationVoid} — a quotation is cancelled, never removed, so its
   * number stays in the continuous `NNN/YY` series (§6).
   */
  quotation: (id: number | string) => `/quotations/${id}`,
  /**
   * POST — body `{ reason }` (mandatory, 3–500 chars), → `{ data }`: the
   * cancelled document, with `status: "VOID"`, `voidedAt` and `voidReason` on it.
   *
   * Capability `quotation.void` (§2.1), held by the OWNER as well as the ADMIN
   * since §2's amendment. Refused with a 409 — naming the bill — for any
   * quotation an invoice was raised from, and for a second void. Terminal: no
   * status change leaves `VOID` (§4).
   */
  quotationVoid: (id: number | string) => `/quotations/${id}/void`,
  /** POST — body `QuotationStatusUpdateSchema`. → `{ data }`. */
  quotationStatus: (id: number | string) => `/quotations/${id}/status`,
  /**
   * POST — **NO BODY** → `{ data: QuotationEmailResult }` (§17A.2 stage 1).
   *
   * ═════════════════════════════════════════════════════════════════════════
   * SENDS THE QUOTATION TO THE CUSTOMER'S OWN EMAIL ADDRESS, WITH THE
   * SERVER-RENDERED PDF ATTACHED — and every part of that is the server's.
   *
   * THE REQUEST CARRIES NOTHING. No recipient, no subject, no message body:
   * the address is read off the customer row by the API, and the schema is a
   * STRICT empty object, so a client that posts `{"to": ...}` gets a 400 rather
   * than having it silently dropped. That is what stops the route being an
   * authenticated relay sending from the business's own domain — whose
   * reputation §17A.3 says must be protected before a single message goes out.
   *
   * IT MOVES THE DOCUMENT. A DRAFT quotation becomes SENT, because sending IS
   * the moment the app knows the offer went out; nothing else moves, and an
   * APPROVED one is never walked backwards. So the caller must invalidate the
   * quotation detail and the register — `statusChanged` says whether it needs to.
   *
   * CAPABILITY `mail.send` (§2.1), NOT a `quotation.*` verb: authoring an offer
   * and putting it in a customer's inbox are different grants. Behind the `mail`
   * flag AND the `quotations` flag, both ahead of the capability — so while
   * either module is off this answers 404, never 403 (§18.2).
   *
   * WHAT ITS FAILURES MEAN: 400 the customer has no email address on file (the
   * message names them), 409 the quotation is cancelled, 502 the mail server
   * refused it — and in every case NOTHING was sent and the document is
   * unchanged.
   * ═════════════════════════════════════════════════════════════════════════
   */
  quotationEmail: (id: number | string) => `/quotations/${id}/email`,

  /**
   * §17.1 — issue (or re-read) the signed link the customer opens, behind the
   * `approval_links` flag and `quotation.share`.
   *
   * A POST for a READ-LOOKING ACT, because the first call MINTS A BEARER
   * CREDENTIAL: whoever holds the returned string can approve this quotation
   * without signing in. Sending `{}` describes the live link; `{ refresh: true }`
   * mints a new one, and the old one keeps working — a QR already printed on a
   * sheet in a customer's file cannot be recalled.
   */
  quotationApprovalLink: (id: number | string) => `/quotations/${id}/approval-link`,

  /**
   * ═════════════════════════════════════════════════════════════════════════════
   * §17.1's PUBLIC ROUTES — the only three endpoints in this file that are
   * reached WITHOUT a session, through `publicPost` / `publicBlob` in lib/api.ts.
   *
   * THEY ARE CONSTANTS, NOT BUILDERS, AND THAT IS THE POINT. There is no id and
   * no token in any of these paths: the token travels in the POST body, because
   * a token in a URL is written in plain text to nginx's access log on the VPS
   * and travels in `Referer`. If one of these ever grows a parameter, something
   * has gone wrong — the quotation is named by the signed token and by nothing
   * else.
   * ═════════════════════════════════════════════════════════════════════════════
   */
  publicApprovalView: "/public/quotations/view",
  publicApprovalPdf: "/public/quotations/pdf",
  publicApprovalDecision: "/public/quotations/decision",
  /**
   * §7.2 amendment (2026-08-15) — unfinished quotations as saved FORMS, the
   * same six routes the bills mount has. See {@link invoiceDrafts}.
   *
   * NOT THE SAME THING AS A QUOTATION WHOSE STATUS IS `DRAFT`. That is a real
   * document: it has spent a number out of the continuous `NNN/YY` series (§6)
   * and it sits in the register. This has no number and is deleted, not voided,
   * when it is abandoned.
   */
  quotationDrafts: "/quotations/drafts",
  quotationDraft: (id: number | string) => `/quotations/drafts/${id}`,

  /* §17.7 — delivery challans, Rule 55. Behind the `challans` flag (§18), so
   * every one of these is a 404 rather than a 403 while the feature is off. */
  /**
   * GET `?status&challanType&openOnly&overdueOnly&customerId&q&from&to&
   * minTotalPaise&maxTotalPaise&sort&dir&limit&offset`
   * → `{ data, total, limit, offset }`. `ChallanListQuerySchema` is `.strict()`.
   *
   * ───────────────────────────────────────────────────────────────────────────
   * `?openOnly=true&customerId=` IS THE INVOICE EDITOR'S DELIVERY PICKER, and
   * that is not an incidental reuse. `openOnly` ANDs `status: OPEN`,
   * `linkedInvoiceId: null` and a BILLABLE challan type — which is exactly the
   * set `POST /invoices` will accept in `challanIds` (§17.7 rule 2). One
   * predicate, `unbilledChallanWhere()` in apps/api/src/services/challans.ts,
   * feeds this filter, the home-screen count and the server's own eligibility
   * check, so the picker cannot offer a row the save would then refuse with a
   * 409.
   *
   * Adding a customer to that query narrows it to the buyer being billed —
   * naming another buyer's challan is refused, so offering one would be an
   * invitation to a 409 nobody could explain.
   * ───────────────────────────────────────────────────────────────────────────
   */
  challans: "/challans",
  challan: (id: number | string) => `/challans/${id}`,
  challanPreview: "/challans/preview",
  /** Marks the goods returned — `challan.edit`, not a capability of its own. */
  challanReturn: (id: number | string) => `/challans/${id}/return`,
  /** A status with a MANDATORY reason; the number stays consumed (§17.4). */
  challanCancel: (id: number | string) => `/challans/${id}/cancel`,
  /**
   * GET `?copy=ORIGINAL|DUPLICATE|TRIPLICATE` — the challan as paper. Returns a
   * document, not JSON; do not call it through `api.get`, and never as an
   * `<a href>` (the route is bearer-authenticated — see `requestBlob`).
   *
   * `?copy=` IS NOT COSMETIC HERE. Rule 55(1) requires THREE copies and says who
   * holds each: ORIGINAL FOR CONSIGNEE, DUPLICATE FOR TRANSPORTER, TRIPLICATE
   * FOR CONSIGNER (§17.7). The DUPLICATE is the one the driver must legally be
   * carrying, so the print menu on the detail screen offers all three by name.
   *
   * Unlike an invoice there is no `copy_type` COLUMN to fall back on, so a
   * request with no `copy` prints ORIGINAL.
   *
   * `challan.view`, the shared read capability — printing an existing challan is
   * reading it. Contrast {@link challanPreviewPdf}.
   */
  challanPdf: (id: number | string) => `/challans/${id}/pdf`,
  /**
   * POST — the UNSAVED form rendered as a DRAFT-watermarked PDF (§17.7). Body is
   * `ChallanCreateSchema`; `?draft=` names the sheet `DRAFT/CHAL/{id}`. Mutates
   * nothing and moves no counter.
   *
   * WHY THE CHALLAN EDITOR NEEDS THIS MORE THAN THE BILL DOES. The
   * consigner/consignee swap is INVISIBLE until the sheet is printed: on
   * `JOB_WORK_IN` the CUSTOMER is the consigner, and nothing on the editor screen
   * shows the party blocks that carry it. The paper goes to a driver.
   *
   * `challan.create`, NOT `challan.view`: it renders a document that does not
   * exist, which is part of AUTHORING one (§2.1 — the ACCOUNTANT holds the read
   * row and not this).
   */
  challanPreviewPdf: "/challans/preview-pdf",
  challanDrafts: "/challans/drafts",
  challanDraft: (id: number | string) => `/challans/drafts/${id}`,
  /**
   * POST / DELETE — the e-way bill number on a CHALLAN. See {@link invoiceEwb},
   * which carries the whole argument; the body and the capability are identical.
   *
   * THE SECONDARY CASE, AND IT IS SECONDARY ON PURPOSE. The CA was asked
   * specifically about works-contract material sent to site — this business's
   * main line, and the case where a delivery challan looks like the obvious
   * basis — and answered "Tax invoice". So the EBN belongs on the INVOICE. This
   * route is for Rule 55 movements with NO invoice behind them.
   *
   * Allowed on RETURNED and INVOICED as well as OPEN: the goods already moved,
   * and a number that arrives after the lorry is back is still the record of
   * that movement. Refused with a 409 on CANCELLED.
   */
  challanEwb: (id: number | string) => `/challans/${id}/ewb`,

  /* §17.3 — CREDIT NOTES, section 34(1) CGST. Behind the `credit_notes` flag
   * (§18), which ships DISABLED, so every one of these answers 404 rather than
   * 403 until it is switched on. Four routes and no more — see below. */
  /**
   * GET `?q&status&invoiceId&customerId&from&to&minTotalPaise&maxTotalPaise&
   * sort&dir&limit&offset` → `{ data, total, limit, offset }`.
   * `CreditNoteListQuerySchema` is `.strict()`, so an unknown key is a 400.
   *
   * **POST** — body `CreditNoteCreateSchema` → 201 `{ data, warnings }`.
   *
   * ───────────────────────────────────────────────────────────────────────────
   * WHAT THE CLIENT MAY NOT SEND, and every omission is load-bearing (§6, §34):
   * no number (allocated under the company-row lock from its own `CRN/26-27/n`
   * series), no status (a note opens ISSUED), no `customerId` (read off the
   * invoice — a credit note cannot be addressed to anyone but the buyer who was
   * billed), no `placeOfSupply` and no `supplyType` (COPIED from the original,
   * so a buyer who has moved state since cannot flip CGST+SGST into IGST), no
   * rates (resolved as of the ORIGINAL INVOICE'S date, §8.7) and no totals.
   *
   * The body is `{ invoiceId, date, reason, attnName?, notes?, lines }`, where
   * `lines` is the very `DocumentLineSchema` an invoice's lines are validated
   * by — one schema, one engine, both surfaces (§2.1).
   *
   * `?invoiceId=` ON THE GET IS WHAT THE BILL'S OWN SCREEN ASKS FOR: every note
   * raised against one invoice. It is the same predicate the server's
   * `sumCreditNotes` folds, so the list under the bill and the credited figure
   * beside it cannot disagree.
   *
   * A 409 FROM THE POST IS INFORMATION, NOT A TRANSIENT FAILURE — the original
   * is a DRAFT or CANCELLED, or the sum of notes would exceed the bill. Never
   * retry it; show the server's sentence, which already names which.
   * ───────────────────────────────────────────────────────────────────────────
   */
  creditNotes: "/credit-notes",
  /** GET → `{ data }`. There is deliberately no PUT: see {@link creditNotes}. */
  creditNote: (id: number | string) => `/credit-notes/${id}`,
  /**
   * POST — body `{ reason }` (1–500 chars), → `{ data }`.
   *
   * A STATUS, NEVER A DELETE, and the number stays consumed (§2, §34(1)'s
   * consecutive series). IT PUTS THE MONEY BACK: every sum over `credit_notes`
   * counts ISSUED rows only, so this one status change returns the credited
   * amount to what the customer owes — on the bill, the unpaid register, the
   * statement and the home screen at once, because all four fold one producer.
   */
  creditNoteCancel: (id: number | string) => `/credit-notes/${id}/cancel`,
  /**
   * GET — the credit note as paper. Returns a document, not JSON; do not call it
   * through `api.get`, and never as an `<a href>` (bearer auth — `requestBlob`).
   *
   * NO `?copy=`. Rule 55(2)'s three captioned copies are a CHALLAN rule and
   * `credit_notes` has no `copy_type` column; one sheet exists, and offering a
   * "Duplicate" that prints the identical page would be a menu inventing a
   * distinction the document does not have.
   */
  creditNotePdf: (id: number | string) => `/credit-notes/${id}/pdf`,
  /**
   * POST — the UNSAVED form rendered as a DRAFT-watermarked PDF (§17.3, s.34(1)).
   * Body is `CreditNoteCreateSchema`, the very schema the save takes, so a form
   * that previews cannot be one the save then rejects. `?template=` as on the
   * GET above. Returns a document, not JSON.
   *
   * ───────────────────────────────────────────────────────────────────────────
   * IT MATTERS MORE HERE THAN ON ANY OTHER DOCUMENT, and the reason is that this
   * one has no draft state. A credit note is ISSUED the moment it is saved: it
   * spends a number out of the consecutive `CRN/26-27/n` series, it is declared
   * in that month's GSTR-1 (CDNR), there is no PUT and no `creditnote.edit` for
   * anyone (§2.1) — the only correction is to cancel it and raise another. So
   * this is the last moment a mistake costs nothing, on the document that exists
   * to fix a mistake.
   *
   * THE SERVER RECOMPUTES EVERYTHING IT PRINTS. The editor's running total is
   * `@steelbill/tax` in the browser, which is right and stays; what the browser
   * cannot know is that the rates resolve as of the ORIGINAL INVOICE'S date
   * against the rates that bill actually charged, and that the split is the
   * original's. Never send totals — `z.object` strips them anyway.
   *
   * IT SPENDS NOTHING. No number, no row, no audit entry (§5, §6, §13). The
   * number box prints plain `DRAFT`: there is nothing to predict and nothing to
   * name, because there are no credit-note drafts.
   *
   * `creditnote.create`, NOT `creditnote.view` — previewing an unsaved document
   * is part of authoring one, and the ACCOUNTANT holds the read row (§2.1).
   * Behind `credit_notes`, which ships DISABLED, so it 404s until switched on.
   * ───────────────────────────────────────────────────────────────────────────
   */
  creditNotePreviewPdf: "/credit-notes/preview-pdf",
  /*
   * NO `creditNotePreview` (the JSON one), NO `creditNoteDrafts`, NO PUT AND NO
   * DELETE — and the absences are the design, not a to-do list.
   *
   * The API mounts four routes in `routes/credit-notes.ts` plus the two PDF ones
   * in `routes/documents-pdf.ts`. There is no JSON preview because the figures
   * on this editor come from `@steelbill/tax` in the browser (the same engine
   * the server runs) and the SHEET is what the owner needed to see. A credit
   * note is never edited, exactly as an issued invoice is not, and
   * `creditnote.edit` does not exist as a capability for anyone to hold (§2.1).
   * It is never hard-deleted, because §34(1) requires a consecutive series and a
   * missing number is a question from the department. There are no drafts: it is
   * short, and `DraftKind` is a database enum, so adding one later is a
   * deliberate `ALTER TABLE` rather than a state that exists because it was
   * cheap. Adding a path here that the server does not serve would be a promise
   * this file cannot keep.
   */
  /** POST — body `QuotationConvertSchema`. §7.1, the one-tap conversion. */
  quotationConvert: (id: number | string) => `/quotations/${id}/convert`,
  /**
   * **POST** — the conversion DRY RUN. Body is `QuotationConvertSchema`, the very
   * schema {@link quotationConvert} takes, so the answer describes THIS request
   * rather than a lenient approximation of it. → `{ data, warnings }` where
   * `data` is a `QuotationConvertPreview` from `@steelbill/types`.
   *
   * ───────────────────────────────────────────────────────────────────────────
   * IT SPENDS NOTHING. Conversion is a one-way door: it allocates an invoice
   * number, and §6 never releases one — a cancelled invoice keeps its number,
   * because a gap is a question from the department. This runs the same plan
   * (same line selection, same pipeline, same rate resolution) and writes no
   * row, no counter, no audit entry.
   *
   * THERE IS NO NUMBER ON THE RESPONSE, not even a nullable one. A caption
   * mistakable for an allocated invoice number is the one promise a preview is
   * not allowed to make (§11.1).
   *
   * IT IS ADVISORY, and the client must not present it as a guarantee. The dry
   * run takes no row lock — a lock needs a transaction, and a preview that
   * opened one would queue behind the writers it exists to protect. The real
   * conversion re-runs the identical plan under `SELECT … FOR UPDATE` and is
   * the authority.
   *
   * A 409 FROM IT IS INFORMATION, NOT A TRANSIENT FAILURE ("this quotation is
   * already converted in full"). Do not retry it, and do not paint it red.
   * ───────────────────────────────────────────────────────────────────────────
   *
   * Capability `quotation.convert` — previewing a conversion is part of
   * performing one, so it is never a capability of its own. Behind BOTH the
   * `quotations` and `invoices` flags, exactly like the commit, so it cannot
   * answer 200 where the conversion answers 404 (§18.2).
   */
  quotationConvertPreview: (id: number | string) => `/quotations/${id}/convert/preview`,
  /** GET — the PDF. Returns a document, not JSON; do not call through `api.get`. */
  quotationPdf: (id: number | string) => `/quotations/${id}/pdf`,
  /**
   * GET — the SAME sheet with the approve/reject QR printed bottom right.
   *
   * A RENDER OPTION ON THE ONE PDF ROUTE, not a second endpoint: there is one
   * place a quotation is drawn and it stays that way. The ordinary download above
   * is deliberately a clean sheet — this is the copy printed and handed to a
   * customer to scan.
   *
   * It cannot mint anything. The URL behind the code is rebuilt from a stored
   * `jti`, so a quotation with no live link — never issued, expired, or already
   * answered — prints the ordinary sheet.
   */
  quotationApprovalSheet: (id: number | string) => `/quotations/${id}/pdf?approvalQr=true`,
  /**
   * POST — the UNSAVED form rendered as a DRAFT-watermarked PDF (§9.3). Body is
   * `QuotationCreateSchema`; `?draft=` names the sheet `DRAFT/QUOT/{id}`. Mutates
   * nothing and consumes no number out of the continuous `NNN/YY` series (§6).
   *
   * IT MATTERS MORE HERE THAN ON A BILL. This is the document that goes to the
   * customer, and §9.3 means what it PRINTS differs from what the editor shows:
   * under `TAX_EXTRA` — the default — the sheet prints "SGST 9% EXTRA" and a
   * PRE-TAX `G. Total`, while the totals bar shows computed tax. The server
   * resolves that presentation itself from `taxDisplayMode`; nothing here picks
   * between the two totals.
   *
   * `quotation.create`, NOT `quotation.view`: previewing an unsaved document is
   * part of authoring one.
   */
  quotationPreviewPdf: "/quotations/preview-pdf",

  /* ------------------------------------------------------------ invoices */
  /**
   * GET `?status&customerId&from&to&issuedManually&limit&offset`
   * → `{ invoices, total, limit, offset }` (envelope key `invoices`, not `data`).
   *
   * THERE IS NO `fy`. `InvoiceListQuerySchema`
   * (apps/api/src/routes/invoices.ts) is `.strict()` and would answer 400 for
   * it. A financial year is expressed as a `from`/`to` pair of calendar days;
   * a customer is `customerId`.
   *
   * THERE *IS* A `q`, and this note used to deny it. It matches the bill's own
   * reference numbers, and `InvoiceSearchBox` on the register sends it. Reading
   * the old wording, someone building a scanner path would have written a
   * page-local filter instead — which looks like search and silently misses
   * every row that is not on the fetched page.
   */
  invoices: "/invoices",
  /**
   * POST — body `InvoiceCreateSchema`. → `{ data, warnings }` ({@link DocumentPreview}).
   *
   * The last moment a mistake is free. An issued invoice is never edited and
   * never deleted (§2, §2.1), so this is the only screen on which a wrong line
   * costs nothing. It consumes NO number (§6) and writes nothing.
   */
  invoicePreview: "/invoices/preview",
  /**
   * GET → `{ data }`. **PUT** → `{ data, warnings }` — and read the next
   * paragraph before writing either.
   *
   * ───────────────────────────────────────────────────────────────────────────
   * THE PUT DOES NOT AMEND AN ISSUED BILL, AND CANNOT. §2.1 defines no
   * `invoice.edit` for any role, deliberately: an issued tax invoice is
   * corrected by a credit note (§17.3) or withdrawn by a cancellation (§17.4),
   * never rewritten. This route is gated on the SEPARATE `invoice.edit_draft`
   * capability and refuses anything but a DRAFT with a 409.
   *
   * WHAT IT IS FOR. Converting a quotation without the customer's S.O. number
   * produces a DRAFT invoice (§7.1) — its number already spent, the S.O.
   * arriving weeks later with their purchase order. This is how that number
   * gets filled in, and it is the whole of the feature.
   *
   * BODY = `InvoiceDraftUpdateSchema` (`@steelbill/types`), `.strict()` with
   * exactly four optional keys: `soNumber`, `soDate`, `challanNumber`,
   * `challanDate`. An absent key leaves the column alone; `null` or `""` clears
   * it. Every other field — lines, totals, customer, date, number, status — is
   * a 400, not a silent ignore.
   *
   * `warnings` carries §9.2's "No S.O. number was entered…" sentence while the
   * row still has none. The response `status` is still `DRAFT`: completing the
   * header is not issuing the bill, and there is no route in this app that
   * does.
   * ───────────────────────────────────────────────────────────────────────────
   */
  invoice: (id: number | string) => `/invoices/${id}`,
  /** POST — §6.1, the bills he wrote by hand. Needs `invoice.set_counter`. */
  invoiceRecordManual: "/invoices/record-manual",
  /**
   * §7.2 amendment — unfinished bills as saved forms. GET `?status=DRAFT|READY`
   * → `{ data }` (list or one). POST `{ payload, customerName }` → 201. PUT adds
   * `version` and 409s a stale save WITH the winning row in the body. DELETE is
   * idempotent.
   */
  invoiceDrafts: "/invoices/drafts",
  invoiceDraft: (id: number | string) => `/invoices/drafts/${id}`,
  /**
   * POST — `{ status: "DRAFT" | "READY" }`, the deliberate act of marking a
   * saved form checked (§7.2). `base` is {@link invoiceDrafts} or
   * {@link quotationDrafts}: the two mounts carry the identical six routes, and
   * spelling `/status` once is what stops one of them growing a second spelling.
   *
   * NEVER SENT ON THE SAVE. `POST`/`PUT` on the draft are `.strict()` and refuse
   * a `status` key, because those two are the autosave — a status riding a
   * debounced write would flip as a side effect of typing. It also does NOT move
   * `version`, so a screen mid-autosave keeps the number it already holds.
   */
  draftStatus: (base: string, id: number | string) => `${base}/${id}/status`,
  /**
   * POST — body `{ reason }`. Capability `invoice.cancel`, held by the OWNER as
   * well as the ADMIN since §2's 2026-08-13 amendment. The only way an invoice
   * is ever withdrawn: the number is retained for ever (§4, §6).
   */
  invoiceCancel: (id: number | string) => `/invoices/${id}/cancel`,
  /** GET — the PDF. Returns a document, not JSON. */
  invoicePdf: (id: number | string) => `/invoices/${id}/pdf`,
  /**
   * POST — the UNSAVED form rendered as a DRAFT-watermarked PDF (§7.1a). Body
   * is `InvoiceCreateSchema`; `?draft=` names the sheet `DRAFT/INV/{id}`.
   * Mutates nothing and moves no counter.
   */
  invoicePreviewPdf: "/invoices/preview-pdf",
  /**
   * **POST** — record the e-way bill number the PORTAL issued (Rule 138).
   * **DELETE** — clear it. Both → `{ data: InvoiceDetail }`.
   *
   * ───────────────────────────────────────────────────────────────────────────
   * THIS APP NEVER GENERATES AN E-WAY BILL AND NEVER WILL. Direct EWB API access
   * needs turnover above ₹10 crore; below that it needs a paid GSP, and this
   * business is far below either. The CA generates it on the government portal,
   * the portal issues the 12-digit number AND its own PDF carrying NIC's QR, and
   * THAT sheet travels with the driver. What this route stores is a record of a
   * fact the government already created.
   *
   * THE NUMBER RECORDED HERE IS DRAWN AS A QR ON THE INVOICE PDF (owner's
   * decision 2026-08-17, §10.1 amendment). The e-way bill QR is plain data —
   * EBN, supplier GSTIN, generation date, no signature — which TallyPrime also
   * builds locally, so it asserts nothing about who raised the bill. NOTHING ON
   * THIS SCREEN draws one: the officer scans paper, so the code belongs on the
   * sheet, not in the browser. (The E-INVOICE IRN QR is the signed one that
   * genuinely cannot be reproduced. Different artifact, different columns, and
   * still only rendered verbatim if a CA ever fills them — see `irn`.)
   *
   * BODY: `{ ewbNo, ewbDate, ewbValidUntil? }`, `.strict()`, with `ewbNo`
   * validated by `EwayBillNumberSchema` (`@steelbill/types`) — exactly 12
   * digits, SHAPE ONLY. There is no checksum and no API to verify against, so a
   * well-formed number is as far as validation can honestly go; the LENGTH check
   * is the one that earns its keep, because a truncated paste is the failure a
   * copy actually produces.
   *
   * RECORDED AFTER THE FACT, NEVER BEFORE DISPATCH. No lorry waits for this
   * screen. Establish that before designing any flow around it.
   *
   * A 409 IS INFORMATION, NOT A TRANSIENT FAILURE: the bill is cancelled, or it
   * already carries a DIFFERENT number. Re-recording the SAME number is
   * idempotent and answers 200. The clearing path is the portal's
   * cancel-and-regenerate case, and the old number survives in the audit row's
   * before-image, which is why there is no `ewb_cancelled_at` column.
   *
   * Capability `eway.record` — NOT `invoice.create`. Recording a fact the
   * government already issued is not privileged authorship, and the CA who types
   * it has no business raising invoices (§2.1).
   * ───────────────────────────────────────────────────────────────────────────
   */
  invoiceEwb: (id: number | string) => `/invoices/${id}/ewb`,

  /* ------------------------------------------------------------ payments */
  /*
   * §17.5. EVERY ONE OF THESE IS BEHIND `requireFlag("payments")`, which runs
   * BEFORE `requireCap` and answers 404 rather than 403 (§18.3) — so a 404 here
   * means "the module is off", never "no such bill". There is no `useFlag` on
   * the client and `features/payments` does not invent one; it reads the 404.
   *
   * THEY HANG OFF THREE DIFFERENT NOUNS AND THAT IS DELIBERATE (see the header
   * of apps/api/src/routes/payments.ts). A receipt is recorded against the
   * INVOICE the money is for; the ledger row it creates is deleted by its OWN
   * id, because deleting a payment is not an operation on the tax invoice; and
   * the outstanding position is about no single invoice at all.
   *
   * NOTHING HERE PUTS A BALANCE *ON* A DOCUMENT. A tax invoice does not carry a
   * running balance (§17.5), which is why `GET /invoices/:id` answers `payment`
   * as a SIBLING of `data` rather than a field inside it, and why the detail
   * screen reads the ledger from its own endpoint instead.
   */
  /**
   * GET → `{ data, summary }`, where `data` is the receipts OLDEST FIRST.
   * POST — body `PaymentCreateSchema` (`.strict()`; `invoiceId` is NOT a field,
   * it comes from the URL) → 201 `{ data, summary }`.
   *
   * THE POST IS REFUSED WITH A **409** for a cancelled or draft bill and for an
   * overpayment, and the server's message names the outstanding amount already
   * Indian-grouped — show it verbatim rather than composing a second sentence.
   * A 409 is information, not a transient failure: never retry it.
   *
   * The GET answers for a CANCELLED invoice too (`isOwed: false`,
   * `outstandingPaise: 0`), because the receipts happened and history is not
   * deleted to make a total tidy (§17.5 rule E).
   */
  invoicePayments: (id: number | string) => `/invoices/${id}/payments`,
  /**
   * DELETE → `{ summary }` — the invoice's position AFTER the row is removed.
   *
   * AN ENTRY-ERROR FIX, NOT A REFUND. §17.5 models no refunds and forbids
   * inventing a negative payment to represent one, so this is a real delete and
   * the audit row (`payment.delete`) is what survives it. No reason is required:
   * nothing was ever shown to a customer to retract.
   */
  payment: (id: number | string) => `/payments/${id}`,
  /**
   * GET → `{ data: OutstandingInvoiceRow[], byCustomer, totals }`.
   *
   * NO PAGING AND NO QUERY PARAMETERS — the whole point of the figure is that it
   * is complete, and a total over page 1 of 4 is a smaller number than the truth
   * presented as the truth. `totals.outstandingPaise` is the same arithmetic
   * over the same rows as `unpaidBills` on `/dashboard/summary`, so the
   * home-screen line and the list it opens cannot disagree.
   */
  paymentsOutstanding: "/payments/outstanding",
  /**
   * GET → `CustomerStatementResponse`, UNWRAPPED (no `data` key).
   *
   * TWO SCOPES, TWO TOTALS. `totals.billedPaise` / `receivedPaise` /
   * `outstandingPaise` are folds over the bills LISTED — a settled invoice is
   * absent from `invoices`, so the arithmetic on screen is arithmetic the reader
   * can check, and the three subtract exactly. `payments` is wider: EVERY
   * receipt against this buyer, including ones against settled or cancelled
   * bills, so it has its own total in `totals.allReceiptsPaise`. Never render
   * one of those figures captioned as the other's list (§17.5). Answers a
   * titled, EMPTY statement when nothing is owed, rather than a 404 that would
   * read as a missing customer.
   */
  customerStatement: (id: number | string) => `/customers/${id}/statement`,

  /* ------------------------------------------------------------- company */
  /** GET → `{ data }`. PUT → `{ data, warnings }`. Writes need `settings.write`. */
  company: "/company",
  /** PUT — §6.1 counters. Needs `invoice.set_counter`; direction rule applies. */
  companyCounters: "/company/counters",

  /* ------------------------------------------------------- notifications */
  /*
   * ═══════════════════════════════════════════════════════════════════════════
   * ONE PERSON'S INBOX. EVERY ROUTE BELOW READS AND WRITES THE CALLER'S OWN
   * ROWS AND NOTHING ELSE — `userId` comes from the session and is never a
   * parameter, so there is no request shape that can widen it.
   *
   * A NOTIFICATION IS TARGETED AT A PERSON AND CARRIES ITS OWN READ STATE. That
   * is the whole reason it is a table rather than a derived list: "the CA has
   * read it, the owner has not" is a fact somebody has to hold. An in-app
   * MESSENGER is a later build and a different thing — a shared conversation
   * with shared read semantics — and none of it is here.
   *
   * BEHIND `requireFlag("notifications")`, WHICH SHIPS OFF, so every one of
   * these answers **404** rather than 403 while the module is switched off
   * (§18.3). There is no `useFlag` on the client and this feature does not
   * invent one: it reads the 404, exactly as payments and credit notes do.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  /**
   * GET `?unreadOnly&event&limit&offset`
   * → `{ notifications, total, unread, limit, offset }` — envelope key
   * `notifications`, and `unread` alongside `total` so the bell and the list
   * agree without a second request.
   *
   * `NotificationListQuerySchema` is `.strict()`; an unknown key is a 400.
   *
   * UNREAD FIRST, THEN NEWEST FIRST — the server's ordering, not the client's.
   * An unread compliance alert from Tuesday matters more than a read one from
   * this morning, and re-sorting these rows on screen would put the thing still
   * to be dealt with below the thing already handled.
   */
  notifications: "/notifications",
  /**
   * GET → `{ unread }`, and nothing else.
   *
   * ITS OWN ROUTE BECAUSE THE HEADER POLLS IT ON EVERY SCREEN. Reading the
   * badge off {@link notifications} would download fifty rows and their payloads
   * to learn one integer, on a workshop mobile connection, once a minute.
   */
  notificationsUnreadCount: "/notifications/unread-count",
  /**
   * POST → `{ data, marked }`. Idempotent — a second call KEEPS THE FIRST
   * timestamp rather than moving it, because "when did she see it" is the
   * question a compliance alert gets asked and re-reading does not change the
   * answer.
   *
   * SOMEBODY ELSE'S ROW ANSWERS **404, NOT 403**. The caller holds the
   * capability — that is not what is missing — and whether a row exists for
   * another person is not their business. Do not render a "forbidden" message
   * for it.
   */
  notificationRead: (id: number | string) => `/notifications/${id}/read`,
  /**
   * POST → `{ data: { marked } }`.
   *
   * NOT A DELETE, DELIBERATELY. The rows stay, with their `read_at`, so "was
   * anybody ever told this bill needed an e-way bill" still has an answer months
   * later. An inbox that empties itself when cleared cannot answer that, and
   * that answer is the point of the whole feature.
   */
  notificationsReadAll: "/notifications/read-all",

  /* ------------------------------------------------------------ messages */
  /*
   * ═══════════════════════════════════════════════════════════════════════════
   * THE SHARED CONVERSATION between the three people who use this system (§2).
   *
   * EVERY ROUTE BELOW READS OR WRITES THE SAME ROWS FOR EVERYBODY, which is the
   * whole difference from the notification hub above: a notification is
   * addressed TO a person and filtered on the caller's own user id; a message is
   * addressed to the room, and if the three of them cannot see the same sentence
   * it is not a conversation.
   *
   * WHAT IS STILL PER-READER: the WATERMARK (how far each has read — hence the
   * badge) and the attachment's `link`, which the server withholds from a reader
   * whose capabilities would bounce them off the screen it points at. The
   * message itself always shows.
   *
   * BEHIND `requireFlag("messages")`, WHICH SHIPS OFF, so every one of these
   * answers **404** rather than 403 while the module is switched off (§18.3) —
   * the same convention as notifications, payments and credit notes, and the
   * client reads the 404 rather than asking for a flag it cannot see.
   *
   * THERE IS NO DELETE AND NO PUT, and there never will be: `message.delete` and
   * `message.edit` do not exist as capabilities for anyone (§2.1). Do not add a
   * client call for one — there is no route behind it.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  /**
   * GET `?limit=&before=`
   * → `{ messages, total, unread, limit, hasMore }`.
   *
   * `messages` READS OLDEST-LAST — the page renders top to bottom the way it was
   * said. Paging goes BACKWARDS: pass the id of the oldest row on screen as
   * `before` to load what came before it. There is deliberately no `offset`; an
   * offset over a table being appended to shifts under the reader.
   */
  messages: "/messages",
  /**
   * GET → `{ unread }`, and nothing else.
   *
   * ITS OWN ROUTE because the header polls it on every screen — reading the
   * badge off {@link messages} would download a page of messages and their
   * captions to learn one integer, on a workshop connection.
   *
   * WHAT THE NUMBER MEANS: messages somebody ELSE wrote since this reader last
   * looked. Never their own, so sending something cannot light up your own
   * badge.
   */
  messagesUnreadCount: "/messages/unread-count",
  /**
   * POST `{ body, attachment? }` → `{ data }`, 201.
   *
   * `attachment` is `{ type, id }` and nothing else — the CAPTION is read off
   * the document server-side, so "about invoice 26-27/67" is a claim somebody
   * can check. Sending a `label` is a 400, and naming a document that is not
   * there is a 404.
   */
  messageSend: "/messages",
  /**
   * POST `{ upToId }` → `{ data: { lastReadMessageId, unread } }`.
   *
   * Moves the CALLER'S OWN watermark, and only ever FORWARD — a replayed or
   * out-of-order request cannot resurrect a badge that was cleared.
   */
  messagesRead: "/messages/read",

  /* --------------------------------------------------- messages, private */
  /*
   * ═══════════════════════════════════════════════════════════════════════════
   * THE SAME MESSENGER, ADDRESSED TO ONE PERSON (2026-08-18).
   *
   * Everything above describes rows addressed to NOBODY in particular — the room
   * the three of them share. These describe rows addressed to one of them. Same
   * table, same `messages` flag, same body rules, opposite visibility; the room's
   * queries now say `recipient IS NULL` so the two never mix.
   *
   * A CONVERSATION IS NAMED BY THE OTHER PERSON, NOT BY AN ID. There is no
   * conversation id and no route that names both participants, so there is no
   * request the client could even form for a conversation it is not in. The
   * caller is always taken from the session.
   *
   * ONE CAPABILITY, `message.direct`, GATES THE READS AS WELL AS THE WRITES —
   * it is not `message.view`/`message.send`, and the client must not substitute
   * them. A person who may no longer hold private conversations should not keep
   * an open window onto one.
   *
   * 404 IS THE ANSWER TO EVERY REFUSAL ABOUT ANOTHER PERSON — a stranger, a
   * deactivated account, somebody without the capability, and yourself all read
   * the same, deliberately. It is ALSO the answer while the `messages` flag is
   * off (§18.3), which is what {@link isModuleOff} reads. The client therefore
   * cannot tell those apart and must not try to.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  /**
   * GET → `{ conversations, unread }`.
   *
   * IT DOUBLES AS THE PEOPLE DIRECTORY, and that is load-bearing rather than
   * convenient: the only other list of users is `GET /users` behind
   * `user.manage`, which the OWNER does not hold. Everybody he may write to
   * appears here, including those with no history — `lastMessage` is null for
   * those. Each row carries an id, a display name, that row's own `unread` and
   * the newest message, and nothing else: no email, no phone, no role, no
   * account state.
   *
   * `unread` at the top is the sum across every private conversation — the
   * badge. It is deliberately NOT folded into {@link messagesUnreadCount},
   * which still counts the room alone.
   */
  messagesDirect: "/messages/direct",
  /**
   * GET `?limit=&before=` → `{ peerUserId, peerName, messages, total, unread,
   * limit, hasMore }`, and POST `{ body, attachment? }` → `{ data }`, 201.
   *
   * The rows are plain {@link MessageResponse}s — the same shape the room
   * returns, so ONE renderer draws both and the attachment `link` is still
   * resolved per reader.
   *
   * THE RECIPIENT IS IN THE PATH AND MAY NOT BE IN THE BODY. The send schema is
   * `.strict()`, so `{ body, recipientUserId }` is a 400: a client able to name
   * the recipient twice is a client able to name two different ones, and the one
   * the server believed would not be the one the sender saw.
   *
   * A CONVERSATION NOBODY HAS STARTED IS 200 AND EMPTY, NOT 404 — "we have never
   * spoken" and "that person does not exist" are different screens, and the peer
   * name is echoed back so an empty one still has a heading.
   */
  messagesDirectWith: (userId: number | string) => `/messages/direct/${userId}`,
  /**
   * POST `{ upToId }` → `{ data: { lastReadMessageId, unread } }`.
   *
   * PER CONVERSATION, which is the whole point of the separate watermark. One
   * mark across all of them would clear the CA's unanswered question because the
   * son's message happened to be newer. `upToId` must name a message in THIS
   * conversation or the server answers 404.
   */
  messagesDirectRead: (userId: number | string) => `/messages/direct/${userId}/read`,

  /* ------------------------------------------------------------- reports */
  /*
   * ═══════════════════════════════════════════════════════════════════════════
   * §11.2's SECONDARY SCREEN AND §17.4's CA EXPORT — and, unlike payments,
   * credit notes and notifications above, NOT BEHIND A FEATURE FLAG.
   *
   * That is deliberate on the server (apps/api/src/routes/reports.ts states the
   * argument in full): GSTR-1 for August is due around 11 September, and a
   * `requireFlag` left off by accident would answer 404 to the one request with
   * a statutory deadline behind it. So a 404 from any path below is a REAL 404 —
   * an API older than this bundle — and never "the module is dark". The one
   * exception is {@link reportsOutstandingAgeing}, which IS a payments feature.
   *
   * ONE PERIOD GRAMMAR, SHARED BY ALL FIVE:
   *
   *   `?preset=thisMonth|lastMonth|thisFy` — the same three strings the SPA's
   *      `lib/date-presets.ts` already spells, so the screen's chip is handed to
   *      the API unchanged and there is no mapping table to fall out of step.
   *   `?from=&to=` — an explicit INCLUSIVE day range.
   *
   * SENDING BOTH IS A 400, not a precedence rule ("from wins over preset" is a
   * rule nobody reads, and the failure it produces is a CSV for a month the
   * owner was not looking at). The screen therefore sends the PRESET and reads
   * the resolved `period.from`/`period.to` back off the response for its links —
   * one resolver, so the file can never describe a different month from the
   * figures above it.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  /**
   * GET — total billed, tax charged and the counts, NET OF CREDIT NOTES.
   * Answers `ReportSummaryResponse` UNWRAPPED. Needs `report.view`.
   *
   * `received` is ABSENT, never zero, when the `payments` module is off or the
   * caller lacks `payment.view` — the same contract as `unpaidBills` on the
   * dashboard, and for the stronger of its two reasons: "Received: ₹0" is a
   * positive claim that nothing came in, made by a system that is not recording
   * receipts at all.
   */
  reportsSummary: "/reports/summary",
  /**
   * GET — the customer-wise summary, largest net first. `ReportCustomersResponse`,
   * UNWRAPPED. NO PAGING, by design: the point of the table is that its rows sum
   * to the figure on the card above it, and a total over page 1 of 4 is a smaller
   * number than the truth presented as the truth.
   */
  reportsCustomers: "/reports/customers",
  /**
   * GET — the unpaid ledger folded into 0-30 / 31-60 / 61-90 / 90+ days.
   *
   * TAKES NO PERIOD AT ALL, deliberately: outstanding is a to-do list, not a
   * period figure, and the bill worth chasing hardest is the oldest one — which
   * any period scope would hide.
   *
   * THE ONE REPORTS ROUTE BEHIND A FLAG. `requireFlag("payments")` runs before
   * `requireCap("payment.view")`, so a 404 here means the module is off (§18.3)
   * and the chart must render NOTHING — no alert, no retry, and above all no
   * "you do not have permission", which is exactly what the 404 exists to hide.
   * Read it with `isFeatureOff` from `features/payments/use-payments`.
   */
  reportsOutstandingAgeing: "/reports/outstanding-ageing",
  /**
   * GET — §17.4 deliverable 1: every document dated in the period, with its
   * STATUS. "This alone replaces the can-you-send-me-last-month's-bills loop."
   *
   * RETURNS `text/csv`, NOT JSON — do not call it through `api.get`, and never as
   * an `<a href>`: the route is bearer-authenticated, exactly like the PDF
   * routes, and a browser navigation carries no Authorization header. Use
   * `requestBlob` with `headers: { Accept: "text/csv" }` and hand the bytes to
   * `downloadBlob`.
   *
   * `report.export`, not `report.view` — a file leaves the building (§2.1).
   *
   * `?bom=false` turns off the UTF-8 byte-order mark, which is ON by default so
   * that Excel renders a Devanagari customer name rather than mojibake.
   */
  reportsRegisterCsv: "/reports/register.csv",
  /**
   * GET `?section=b2b|cdnr|hsn|docs` — §17.4 deliverable 2, ONE CSV PER TABLE.
   *
   * FOUR FILES RATHER THAN ONE because the four tables have different columns,
   * and stacking them produces a file that is not rectangular — every parser and
   * every spreadsheet reads that as one malformed table. It is also how GSTR-1
   * is actually filed: the offline tool takes the tables separately.
   *
   * `section` IS REQUIRED, with no default, and the screen must offer all four:
   * the one a default would silently omit is CDNR, whose absence over-reports the
   * owner's liability.
   */
  reportsGstr1Csv: "/reports/gstr1.csv",

  /* --------------------------------------------------------------- users */
  /*
   * ═══════════════════════════════════════════════════════════════════════════
   * §2, §13 — ACCOUNT ADMINISTRATION. EVERY ROUTE BELOW NEEDS `user.manage` AND
   * NONE OF THEM IS BEHIND A FEATURE FLAG.
   *
   * The absence of a flag is a decision (apps/api/src/routes/users.ts states it
   * in full): a flag that could 404 this router is a flag that can stop the last
   * administrator fixing a lockout. So a 404 from any path here is a REAL 404 —
   * an API older than this bundle — and never "the module is dark". Do not reach
   * for `FeatureOff` on these screens.
   *
   * THERE IS NO `DELETE`, ON THIS PATH OR ANY OTHER. A user who has issued
   * documents or authored audit rows must not vanish, which is what the schema's
   * `RESTRICT` and `SetNull` foreign keys already say. Deactivation is the
   * operation ({@link user}, `{ isActive: false }`) and it takes effect on the
   * target's very next request, because `authenticate` re-reads the row every
   * time (§14.2). Adding a delete here would be a promise the server does not
   * keep.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  /**
   * GET `?role=&active=&limit=&offset=` → `UserListResponse`
   * (`{ users, total, limit, offset }` — envelope key `users`, like `/items`).
   * `UserListQuerySchema` is `.strict()`, so an unknown key is a 400.
   *
   * **POST** — body `UserCreateSchema` → 201 `{ data, warnings? }`.
   *
   * ───────────────────────────────────────────────────────────────────────────
   * WHICH IDENTIFIER AND WHICH SECRET THE BODY TAKES IS DECIDED BY THE ROLE, ON
   * THE SERVER. `ROLE_CREDENTIAL_PATH` lives in `apps/api/src/auth/`, NOT in
   * `@steelbill/types`, deliberately — it is authentication, and an
   * authentication rule compiled into the SPA is a rule somebody will eventually
   * enforce here, where it is decoration. So the OWNER's row takes
   * `{ phone, pin }` and the ADMIN's and the CA's take `{ email, password }`,
   * and the client learns which from `signInWith` on rows it has already seen
   * (`features/users/use-users.ts`) rather than from a map of its own.
   *
   * Sending the wrong pair is a **400 naming the field the server wanted**. Show
   * that sentence; it is more specific than anything this client could compose.
   *
   * `warnings` IS NEVER AN ERROR AND IS ALWAYS WORTH SHOWING. The one that
   * matters: an ACCOUNTANT created while `accountant_role` is off exists, holds
   * her capabilities, and still cannot sign in (§18.5) — the account and the
   * session are different questions, so the create SUCCEEDS and says so here.
   * ───────────────────────────────────────────────────────────────────────────
   */
  users: "/users",
  /**
   * GET → `{ data }`. **PUT** — body `UserUpdateSchema` → `{ data, warnings? }`.
   *
   * `UserUpdateSchema` is `.strict()` and carries NO credential field, so a
   * rename can never quietly reset a password — that is {@link userCredential},
   * a separate route with its own audit action.
   *
   * FIVE REFUSALS COME BACK AS 403 OR 409 WITH A SENTENCE, and every one of them
   * names what would have happened rather than saying "no": demoting yourself
   * out of `user.manage` (403), switching your own account off (403), leaving
   * nobody able to administer the system (409), moving an account to a sign-in
   * door it cannot open (409), and an identifier that already belongs to
   * somebody (409, on the POST). NONE of them is re-implemented in the client —
   * a second copy of "would this leave nobody able to administer?" needs a count
   * the browser does not have, and would disagree with the server on the day it
   * mattered. Send the request; render `error.message` verbatim.
   */
  user: (id: number | string) => `/users/${id}`,
  /**
   * POST — body `UserCredentialSchema` (`{ pin }` **or** `{ password }`, exactly
   * one, matching the target's role) → `{ data, warnings? }`.
   *
   * ONE ROUTE FOR BOTH DOORS, because it is one operation and splitting it would
   * be a second place for the audit row, the lockout reset or the hashing call
   * to be forgotten.
   *
   * IT IS ALSO THE ONLY UNLOCK THIS API HAS. Setting a credential clears
   * `failed_login_count` and `locked_until` (§11A) — there is deliberately no
   * "unlock but keep the password" route, because an account freezes precisely
   * when somebody has been guessing at it. Say so beside the field.
   *
   * A SECRET TRAVELS IN AND NEVER COMES BACK OUT. `UserResponse` carries
   * `hasCredential`, a boolean ABOUT a hash and never anything derived from one.
   * Never display, echo, log or store what was typed here.
   */
  userCredential: (id: number | string) => `/users/${id}/credential`,

  /* --------------------------------------------------------------- audit */
  /**
   * GET `?actorUserId=&entityType=&entityId=&action=&from=&to=&limit=&offset=`
   * → `AuditListResponse`. `audit.view`, ADMIN-only (§2.1), no feature flag.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   * THERE IS NO `total` ON THE RESPONSE, AND ITS ABSENCE IS THE DESIGN. A
   * `COUNT(*)` over a filtered six-year table is an index scan of every matching
   * row, paid on EVERY page — the one request on this screen that could take the
   * VPS down. `hasMore` answers what the screen actually needs, out of the
   * `limit + 1` rows the query was fetching anyway.
   *
   * SO PAGE ON `hasMore`, NEVER ON A PAGE COUNT. "Page 3 of 40" cannot be
   * rendered without the number this endpoint refuses to compute, and a client
   * that estimated one would be inventing it.
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * `limit` IS CAPPED AT 100 AND `offset` AT 10,000 BY THE SCHEMA, so exceeding
   * either is a clean 400 rather than something a handler clamps. The offset cap
   * is unusual and this is the table that earns it: deep offset paging over an
   * append-only log is a scan whose cost grows with how bored the reader is. The
   * refusal points at `from`/`to`, which are indexed — see `AUDIT_OFFSET_MAX` in
   * `@steelbill/types`, which the screen reads so it can stop before the 400.
   *
   * `entityId` NEEDS `entityType` BESIDE IT (id 5 exists in every table), and
   * every filter is a REAL COLUMN — nothing queries inside `before`/`after`,
   * which are text-backed JSON (CLAUDE.md).
   *
   * READING THIS LOG WRITES NO ROW TO IT, so the screen may refetch freely.
   * There is no `GET /audit/:id`, no POST, no PUT and no DELETE: `audit_log` is
   * append-only, and an endpoint that could edit a row would make the whole
   * table worthless as evidence.
   */
  audit: "/audit",

  /* --------------------------------------------------------------- flags */
  /** GET → the ADMIN feature-flag screen (§18.1). Needs `settings.write`. */
  flags: "/flags",
  /** PUT — toggle one flag. */
  flag: (key: string) => `/flags/${key}`,

  /* -------------------------------------------------------------- health */
  health: "/health",
} as const;

/* ========================================================================== */
/* Response shapes                                                            */
/*                                                                            */
/* Only the ones the SCAFFOLD needs are declared here. A screen author adding  */
/* a new one should mirror the interface from the route that produces it and   */
/* cite the file, exactly as below — these are NOT independent definitions,    */
/* they are transcriptions, and the API is the authority.                      */
/* ========================================================================== */

/** Paged list envelope shared by `/customers` and `/quotations` (key: `data`). */
export interface DataListResponse<T> {
  data: T[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * One row of the quotation register.
 * Transcribed from `apps/api/src/services/quotations.ts` — `QuotationSummary`.
 */
export interface QuotationListRow {
  id: number;
  /** `542/26` — sequence FIRST, unlike an invoice's `26-27/67` (§6). */
  number: string;
  date: string;
  status: string;
  customerId: number;
  /** From the FROZEN snapshot. Empty string, not null, when unreadable. */
  customerName: string;
  subject: string | null;
  validUntil: string | null;
  subtotalPaise: number;
  totalTaxPaise: number;
  grandTotalPaise: number;
  /**
   * §9.3, PRE-RESOLVED BY THE SERVER — and the field the register must show.
   *
   * ─────────────────────────────────────────────────────────────────────────
   * A TAX_EXTRA QUOTATION QUOTES A PRE-TAX FIGURE. The existing Excel documents
   * print "SGST 9% EXTRA" against the tax lines, so the number the customer was
   * told is the SUBTOTAL, and that is what the PDF prints under "G. Total".
   *
   * `printedGrandTotalPaise` is that number, already chosen. Showing
   * `grandTotalPaise` instead would display a figure the customer has never
   * seen and that appears nowhere on their copy — a discrepancy they would
   * raise. This exact mistake has been made FIVE times in this codebase, always
   * by a screen deciding for itself which of the two totals to show, which is
   * why the server resolves it and the client must not re-derive it.
   * ─────────────────────────────────────────────────────────────────────────
   */
  display: {
    taxDisplayMode: string;
    taxIsExtra: boolean;
    /** The figure the customer was quoted. Use THIS one. */
    printedGrandTotalPaise: number;
  };
  /** Set once §7.1's conversion has raised a bill from this quotation. */
  convertedInvoiceId: number | null;
  voidedAt: string | null;
  voidReason: string | null;
  /** False once it is CONVERTED or VOID — both terminal (§4). */
  isEditable: boolean;
  createdAt: string;
  updatedAt: string;
}

/** `GET /quotations` — envelope key `data`, like customers, unlike invoices. */
export interface QuotationListResponse {
  data: QuotationListRow[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * A saved job from the rate card — "My List" (§11.1).
 * `GET /items` → `{ items, total, limit, offset }` (envelope key `items`).
 */
export interface Item {
  id: number;
  name: string;
  description: string | null;
  category: string;
  supplyNature: string;
  /** FK into the versioned HSN master. Reads use `hsn` below (§8.7). */
  hsnId: number;
  /** The HSN/SAC code itself, resolved from the master for the item's rate. */
  hsn: string;
  hsnDescription: string;
  isService: boolean;
  /** BASIS POINTS — `1800` is 18%. */
  gstRateBp: number;
  pricingMode: string;
  unitLabel: string;
  /** INTEGER PAISE. */
  ratePaise: number;
  /** How often it has been billed — the picker orders by it (§11.1). */
  usageCount: number;
  lastUsedAt: string | null;
  /** Only meaningful when `trackStock`; §17.6 is otherwise post-v1. */
  stockQty: number | null;
  trackStock: boolean;
  /** Retired rather than deleted: past invoice lines still point at it. */
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ItemListResponse {
  items: Item[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * `POST /invoices/preview` → `{ data, warnings }`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS IS THE ONLY PLACE THE EDITOR GETS A TOTAL. §6 makes the server the sole
 * authority, and CLAUDE.md forbids reimplementing the tax math in the client
 * "for a quick preview" — precisely because a preview that disagreed with the
 * saved document would be discovered by a customer, not by a test.
 *
 * It writes nothing and consumes NO invoice number (§6), so it can be called on
 * every edit. The figures it returns are computed by the same pipeline the save
 * runs.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export interface DocumentPreviewLine {
  key: string;
  parentKey: string | null;
  lineCode: string;
  sortOrder: number;
  isGroupHeader: boolean;
  itemId: number | null;
  description: string;
  supplyNature: string;
  hsn: string;
  isService: boolean;
  gstRateBp: number;
  pricingMode: string;
  qtyMilli: number;
  unitLabel: string;
  weightGrams: number | null;
  lengthMm: number | null;
  pieces: number | null;
  ratePaise: number;
  /**
   * §15(3)(a) — what the server took off this line, and this line's share of the
   * whole-document discount, which the editor cannot derive: the apportionment
   * is proportional, rounds, and its remainders are handed out by largest
   * remainder in `@steelbill/tax`.
   */
  discountPaise: number;
  apportionedDiscountPaise: number;
  /**
   * §15(3)(a) — the percentage `discountPaise` was worked out from, in basis
   * points, or `null` when an amount was typed.
   *
   * PROVENANCE, NOT AN INSTRUCTION. Nothing on the client may re-apply it: the
   * money above is the record, and the server resolved it once. It is here so
   * the editor can show the `(10%)` bracket the sheet will print — and only when
   * it will; see `bracketPercentBp` in features/invoices/DiscountField.tsx.
   *
   * `null` AND `0` ARE DIFFERENT FACTS. `0` is "he typed a percentage of
   * nought"; `null` is "he typed rupees", which is every discount this business
   * issued before the percentage box existed, and which prints no bracket.
   */
  discountPercentBp: number | null;
  /** NET of both — the value of supply this line's GST was charged on. */
  taxablePaise: number;
  cgstPaise: number;
  sgstPaise: number;
  igstPaise: number;
  lineTotalPaise: number;
  isAdhoc: boolean;
}

export interface DocumentPreview {
  kind: string;
  date: string;
  customerId: number;
  customer: DocumentCustomerSnapshot;
  /** `INTRA_STATE` | `INTER_STATE` — the SERVER decided this, from the states. */
  supplyType: string;
  placeOfSupply: string;
  lines: DocumentPreviewLine[];
  totals: DocumentTotals;
}

/** Every preview and save answers this — `warnings` is advice, not an error. */
export interface PreviewEnvelope<T> {
  data: T;
  warnings?: string[];
}

/**
 * A customer, as `GET /customers` returns it.
 *
 * THIS IS THE LIVE ROW, not a document's frozen snapshot. Editing it changes
 * what future documents will print and nothing about past ones (§4) — see
 * {@link DocumentCustomerSnapshot}, which is the other thing and must never be
 * confused with this.
 */
export interface Customer {
  id: number;
  name: string;
  gstin: string | null;
  /** Rule 114B — typed only for a no-GSTIN buyer; registered buyers derive it. */
  pan: string | null;
  /** `27` — the two-digit GST state code, which decides CGST+SGST vs IGST (§6). */
  stateCode: string;
  stateName: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  pincode: string | null;
  phone: string | null;
  email: string | null;
  /** GST-registered. An unregistered buyer has no GSTIN and is billed all the same. */
  isRegistered: boolean;
  /** The code THIS customer files us under — it prints on their copy. */
  vendorCode: string | null;
  defaultContactName: string | null;
  /** Archived rather than deleted: past documents still reference them. */
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * `GET /customers` — note the envelope key is `data`, unlike `/invoices` which
 * answers `invoices`. The API is not uniform here and the client must not
 * pretend it is.
 */
export interface CustomerListResponse {
  data: Customer[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * The buyer, FROZEN onto the document the day it was made (§4).
 * Transcribed from `apps/api/src/services/documents.ts` — `CustomerSnapshot`.
 *
 * NEVER re-fetch the live customer to render a document. If Lupin moves office
 * or corrects its GSTIN, every invoice already issued must keep printing the
 * address and GSTIN it was issued with — that is what the customer holds and
 * what the CA filed. This is the whole reason the column exists.
 */
export interface DocumentCustomerSnapshot {
  snapshotVersion: number;
  customerId: number;
  name: string;
  gstin: string | null;
  /**
   * Rule 114B's typed PAN — populated ONLY for a buyer with no GSTIN.
   *
   * The server has frozen this onto every document since 2026-08-15
   * (`customerSnapshot` writes `pan: gstin === null ? customer.pan : null`), but
   * this type did not declare it, so the field arrived on the wire and was
   * invisible to the client. A registered buyer's PAN is derived from the GSTIN
   * instead — see `panFromGstin` in `@steelbill/types`, which is what both the
   * detail screens and the PDF use, so screen and paper cannot disagree.
   */
  pan: string | null;
  isRegistered: boolean;
  stateCode: string;
  stateName: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  pincode: string | null;
  phone: string | null;
  email: string | null;
  vendorCode: string | null;
}

/**
 * One line of a document.
 *
 * MONEY IS INTEGER PAISE, QUANTITY IS MILLI-UNITS (`1.000` → `1000`), and the
 * GST rate is BASIS POINTS (`18%` → `1800`). Nothing here is a float and nothing
 * here may be divided outside `@steelbill/tax`.
 *
 * `gstRateBp` is the rate SNAPSHOT taken when the document was saved (§8.7), not
 * today's rate for the HSN code. An issued invoice stays arithmetically correct
 * forever precisely because this number never moves.
 */
export interface DocumentLine {
  id: number;
  lineCode: string | null;
  sortOrder: number;
  parentLineId: number | null;
  /** A heading with sub-lines under it — it carries no amount of its own. */
  isGroupHeader: boolean;
  itemId: number | null;
  description: string;
  supplyNature: string;
  hsn: string;
  isService: boolean;
  /** BASIS POINTS — `1800` is 18%. */
  gstRateBp: number;
  pricingMode: string;
  /** MILLI-UNITS — `1000` is 1.000. */
  qtyMilli: number;
  unitLabel: string;
  weightGrams: number | null;
  lengthMm: number | null;
  pieces: number | null;
  ratePaise: number;
  /**
   * §15(3)(a) — the discount typed on THIS line, and this line's apportioned
   * share of the whole-document one. Both zero on an undiscounted line, and on
   * every challan and credit-note line, which cannot carry either.
   *
   * `taxablePaise` below is already NET of both. That is why the share has to
   * come over the wire at all: it cannot be recovered from the other figures.
   */
  discountPaise: number;
  apportionedDiscountPaise: number;
  /**
   * §15(3)(a) — the percentage `discountPaise` was worked out from, or `null`
   * when an amount was typed. Provenance only: the money beside it is what was
   * taxed and what §15(3)(a) requires to be recorded, and no client re-applies
   * this. Always `null` on a challan and a credit-note line, which cannot carry
   * a discount at all.
   */
  discountPercentBp: number | null;
  taxablePaise: number;
  cgstPaise: number;
  sgstPaise: number;
  igstPaise: number;
  lineTotalPaise: number;
  isAdhoc: boolean;
  savedAsItemId: number | null;
}

/** The document's totals, as the SERVER computed them (§6). Never recompute. */
export interface DocumentTotals {
  subtotalPaise: number;
  cgstPaise: number;
  sgstPaise: number;
  igstPaise: number;
  totalTaxPaise: number;
  roundOffPaise: number;
  grandTotalPaise: number;
  /**
   * §15(3)(a) — the discount figures, all four always present.
   *
   * TRANSCRIBED FROM `DocumentTotalsResponse` (apps/api/src/services/documents.ts),
   * like everything else in this file. A field added there and forgotten here is
   * silently dropped by TypeScript rather than reported, which is exactly how the
   * discount would have reached the printed bill and not the screen.
   *
   * `grossSubtotalPaise` is the value of the lines BEFORE anything came off;
   * `subtotalPaise` above is already net and is what the tax was charged on.
   * `totalDiscountPaise` is the one figure to show — it is the sum of the other
   * two, because one reduction was given in total.
   */
  grossSubtotalPaise: number;
  lineDiscountTotalPaise: number;
  documentDiscountPaise: number;
  /**
   * §15(3)(a) — the percentage `documentDiscountPaise` was worked out from, in
   * basis points, or `null` when an amount was typed.
   *
   * NOT COLLAPSED TO `0` like the four figures around it, because it is not
   * money: `0` would claim a percentage of nought was typed on every discount
   * ever issued as rupees, and the printed band would then carry a `(0%)`
   * bracket nobody wrote.
   */
  documentDiscountPercentBp: number | null;
  totalDiscountPaise: number;
  /** Already rendered by the server, in words, for the printed document. */
  amountInWords: string;
}

/**
 * `GET /invoices/:id` → `{ data }`.
 * Transcribed from `apps/api/src/services/documents.ts` — `InvoiceResponse`.
 */
export interface InvoiceDetail {
  kind: "invoice";
  id: number;
  /** `26-27/67` (§6). */
  number: string;
  /** `YYYY-MM-DD` calendar day. */
  date: string;
  customerId: number;
  customer: DocumentCustomerSnapshot | null;
  attnName: string | null;
  status: string;
  placeOfSupply: string;
  supplyType: string;
  soNumber: string | null;
  soDate: string | null;
  challanNumber: string | null;
  challanDate: string | null;
  quotationRef: string | null;
  quotationRefDate: string | null;
  copyType: string;
  issuedManually: boolean;
  sourceQuoteId: number | null;
  /** An ISO INSTANT, not a calendar day. */
  cancelledAt: string | null;
  cancelReason: string | null;
  /**
   * Rule 138 — the e-way bill number the PORTAL issued, its date, and its
   * validity when the sheet states one. See {@link endpoints.invoiceEwb}.
   *
   * ─────────────────────────────────────────────────────────────────────────
   * NULL ON NEARLY EVERY BILL, AND THAT IS CORRECT — most jobs are below the
   * threshold. Render the block only when `ewbNo` is present: a blank "E-way
   * Bill No. ____" row is furniture, and on a document it invites somebody to
   * write a number into a printed copy the app then knows nothing about.
   *
   * `ewbDate` and `ewbValidUntil` are CALENDAR DAYS (`YYYY-MM-DD`), like
   * `date` and unlike `cancelledAt` — `useFormatDay`, never `new Date(...)`.
   *
   * THEY ARE REQUIRED KEYS HERE EVEN THOUGH THEY ARE ALMOST ALWAYS NULL. The
   * server sends all three on every invoice; declaring them optional would let
   * a screen forget them and TypeScript agree, which is exactly how the
   * customer's typed PAN reached the wire and stayed invisible to the client
   * for a week (see {@link DocumentCustomerSnapshot.pan}).
   * ─────────────────────────────────────────────────────────────────────────
   */
  ewbNo: string | null;
  ewbDate: string | null;
  ewbValidUntil: string | null;
  /**
   * TRUE ONLY FOR A DRAFT — the server's own answer, from
   * `documentEditabilityRefusal`.
   *
   * `invoice.edit` still does not exist as a capability for anyone (§2.1), and
   * an ISSUED bill is still never amendable. What this flag now gates is the
   * one sanctioned change: `PUT /invoices/:id` completing the header fields
   * conversion left missing on a DRAFT (see {@link endpoints.invoice}).
   *
   * IT HIDES A CONTROL; IT NEVER AUTHORISES ONE. Pair it with
   * `can("invoice.edit_draft")` — both, never one — and the server asserts the
   * status again on the write regardless.
   */
  isEditable: boolean;
  totals: DocumentTotals;
  lines: DocumentLine[];
  notes: string | null;
  terms: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * One quotation, lines and all — `GET /quotations/:id`.
 * Transcribed from `apps/api/src/services/documents.ts` — `QuotationResponse`.
 */
export interface QuotationDetail {
  kind: "quotation";
  id: number;
  /** `542/26` — sequence FIRST, unlike an invoice's `26-27/67` (§6). */
  number: string;
  date: string;
  customerId: number;
  customer: DocumentCustomerSnapshot | null;
  attnName: string | null;
  /** DRAFT | SENT | APPROVED | REJECTED | PARTIALLY_CONVERTED | CONVERTED | EXPIRED | VOID. */
  status: string;
  subject: string | null;
  /** `YYYY-MM-DD`, or null when the offer carries no expiry. */
  validUntil: string | null;
  /** §9.3 — TAX_EXTRA quotes a PRE-TAX figure; TAX_COMPUTED quotes the total. */
  taxDisplayMode: string;
  poRefNo: string | null;
  /** §9.3's `item no` — the customer's own line number for this job, if given. */
  itemNo: string | null;
  /**
   * THE CUSTOMER'S, NOT THE QUOTATION'S. It is edited on the customer screen
   * (`customers.vendor_code`) and copied onto the document at save time (§4), so
   * the editor shows it read-only and never sends it back.
   */
  vendorCode: string | null;
  /** Set once §7.1's conversion has raised a bill from this quotation. */
  convertedInvoiceId: number | null;
  /** An ISO INSTANT, not a calendar day. Both null unless `status` is VOID. */
  voidedAt: string | null;
  voidReason: string | null;
  /**
   * §17.1's approval loop — ISO instants, both null until they happen.
   *
   * `sentAt` is stamped by the FIRST send through any of the three doors, and
   * never moved afterwards: "how long have we been waiting" runs from when the
   * customer first got the offer, not from the owner's third forward of it.
   *
   * `decidedAt` IS NOT DERIVABLE FROM `status`, and that is why it is sent
   * separately. `status` keeps moving — an approved quotation becomes CONVERTED
   * the moment it is billed — while this records that a customer answered, once,
   * on a date, and never changes again.
   *
   * `decidedByName` AND `decisionNote` ARE WHAT THE CUSTOMER ACTUALLY TYPED, and
   * they are here because for a while they were not: the portal invites a note
   * ("for example: please revise the delivery date") and the owner's screen then
   * showed only a date, so a customer's reason for rejecting an offer was
   * readable nowhere outside `audit_log` — behind a capability the owner does
   * not hold. Both are null until a decision is recorded, and null for a
   * customer who left the note box empty.
   *
   * THE IP AND THE LINK'S `jti` ARE STILL NOT SENT. They are provenance for a
   * dispute, they live in the audit trail, and a customer's IP address has no
   * business on a screen.
   */
  sentAt: string | null;
  /**
   * HOW it went out — `EMAIL`, `WHATSAPP`, `LINK` or `RECORDED`.
   *
   * Beside `sentAt` rather than derivable from it: the column exists precisely
   * because a date alone cannot tell "we emailed it on the 4th" from "he says he
   * told them on the 4th", and those are different answers to a customer who
   * says they never received it. Only one can be checked.
   *
   * Null for anything stamped before the column existed, which is why the screen
   * has two sentences rather than one with a blank in it.
   */
  sentChannel: "EMAIL" | "WHATSAPP" | "LINK" | "RECORDED" | null;
  decidedAt: string | null;
  decidedByName: string | null;
  decisionNote: string | null;
  /**
   * WHETHER A `PUT` WOULD BE ACCEPTED RIGHT NOW — the server's own answer, not a
   * rule this screen re-derives. Unlike an invoice, a quotation IS editable, but
   * only while DRAFT or SENT: once it has been approved, rejected, converted,
   * expired or voided it is the record of an offer that was actually made, and
   * rewriting it would rewrite history the customer holds a copy of.
   *
   * The server asserts this again on the `PUT`, so this flag may hide an editor
   * and must never be the thing that authorises one.
   */
  isEditable: boolean;
  totals: DocumentTotals;
  /**
   * §9.3, PRE-RESOLVED BY THE SERVER — see the long note on
   * {@link QuotationListRow.display}. A TAX_EXTRA quotation quotes the SUBTOTAL,
   * and `printedGrandTotalPaise` is already the figure the customer was told.
   * Never choose between `totals.subtotalPaise` and `totals.grandTotalPaise`
   * here; that mistake has been made five times in this codebase.
   */
  display: {
    taxDisplayMode: string;
    taxIsExtra: boolean;
    /** The figure the customer was quoted. Use THIS one. */
    printedGrandTotalPaise: number;
  };
  lines: DocumentLine[];
  notes: string | null;
  terms: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * What `POST /quotations/:id/email` answers with (§17A.2 stage 1).
 *
 * Transcribed from `SentQuotationEmail` in `apps/api/src/services/mail.ts`.
 *
 * `to` IS ECHOED BACK ON PURPOSE, and the screen must say it out loud. The owner
 * pressed one button and an email left the building; "Sent" alone leaves him
 * with no way to notice that it went to a colleague who left the company in
 * March. Naming the address is the difference between a confirmation and a claim.
 */
export interface QuotationEmailResult {
  /** The `mail_messages` row in SENT — the answer to "did we send it?" later. */
  mailMessageId: number;
  /** The address the message actually went to. Show it. */
  to: string;
  subject: string;
  /** The attachment's name, the same one the download would carry. */
  filename: string;
  sentAt: string;
  /** The quotation's status AFTER the send. */
  quotationStatus: string;
  /** True only when DRAFT → SENT actually happened, so the toast can say so. */
  statusChanged: boolean;
}

/* ========================================================================== */
/* Delivery challans — §17.7, Rule 55 CGST                                    */
/*                                                                            */
/* ═══════════════════════════════════════════════════════════════════════════ */
/* A CHALLAN IS NOT A TAX INVOICE, AND NOTHING ON IT IS OUTPUT TAX.           */
/*                                                                            */
/* It carries `subtotalPaise` / `totalTaxPaise` / `grandTotalPaise` because   */
/* Rule 55(1) requires a taxable value — and a rate and amount where the      */
/* movement is for supply — so the lines run through the SAME engine an       */
/* invoice's do. None of it reaches GSTR-3B and none of it is money anyone    */
/* pays. Only the SERIAL RANGE is declared, in GSTR-1 (§17.4), which is why   */
/* cancelling keeps the number. A screen that labels these figures "tax       */
/* payable", "GST" or "amount due" is asserting a liability that does not     */
/* exist; label the figure as the declared value of goods in transit.         */
/* ═══════════════════════════════════════════════════════════════════════════ */
/* ========================================================================== */

/**
 * One row of the challan register.
 * Transcribed from `apps/api/src/services/challans.ts` — `ChallanSummary`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO DIFFERENT TIME SHAPES LIVE ON THIS ONE ROW, and mixing them is a real bug
 * rather than a formatting nit. `date` and `expectedReturnDate` are CALENDAR
 * DAYS (`YYYY-MM-DD`) — hand them straight to `useFormatDay`. `returnedAt`,
 * `cancelledAt`, `createdAt` and `updatedAt` are ISO INSTANTS, and must go
 * through `istDayOfInstant` first: slicing an instant to ten characters takes
 * the UTC day and prints it as an IST one, so anything recorded between 00:00
 * and 05:30 IST reports yesterday.
 *
 * NOTHING HERE IS RE-DERIVED IN THE CLIENT. `direction`, `isOverdue` and
 * `isEditable` are all computed server-side and sent because a second copy of
 * any of them could disagree with the first — see each field.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export interface ChallanListRow {
  id: number;
  /** `CHL/26-27/7` — its own series, reset each financial year (§6). */
  number: string;
  /** `YYYY-MM-DD` — a calendar day in Asia/Kolkata, never an instant. */
  date: string;
  status: ChallanStatus;
  /** The legal basis for the movement under Rule 55(1) — never a category. */
  challanType: ChallanType;
  /**
   * WHICH WAY THE GOODS ARE GOING, derived from `challanType` by the SERVER and
   * never stored (§17.7).
   *
   * `INWARD` for `JOB_WORK_IN` alone, and on that one row the CUSTOMER is the
   * consigner and this business is the consignee — the two address blocks swap.
   * Rule 55(1) requires both parties named, so printing the receiver as the
   * sender is a false document, not a cosmetic slip. Show this field; never
   * recompute it from `challanType` on a screen, because the second copy is
   * precisely what could disagree with the first.
   */
  direction: ChallanDirection;
  customerId: number;
  /**
   * From the document's FROZEN snapshot (§4).
   *
   * EMPTY STRING, NOT NULL, when the snapshot is unreadable — `snapshot?.name ??
   * ""` in `toChallanSummary`. That is the quotation convention, not the
   * invoice one, so `row.customerName ?? t("lbl.customerUnknown")` never fires
   * and leaves a blank cell; test the empty string.
   */
  customerName: string;
  /** As the owner typed it. Never decorated, prefixed or uppercased. */
  vehicleNumber: string | null;
  isReturnable: boolean;
  /** `YYYY-MM-DD` calendar day. */
  expectedReturnDate: string | null;
  /** An ISO INSTANT — the lorry came back on Saturday, entered on Monday. */
  returnedAt: string | null;
  /**
   * Expected back, still out, and the day has PASSED — computed server-side
   * with a `<` boundary, so goods due back TODAY are not yet late.
   *
   * Never recompute this from `expectedReturnDate` against the browser clock:
   * that reintroduces the machine-local timezone bug the service comment warns
   * about, and it would make the register disagree with the `overdueOnly`
   * filter that produced these very rows.
   */
  isOverdue: boolean;
  /**
   * The invoice finally raised against this movement, or null while it is still
   * unbilled. THE ONLY FIELD ON A CHALLAN THAT MAY BECOME A HYPERLINK — a real
   * row exists behind it. `vehicleNumber` and `shipToAddress` are free text.
   */
  linkedInvoiceId: number | null;
  /** INTEGER PAISE. Format through `formatRupees`; see the section note above. */
  subtotalPaise: number;
  totalTaxPaise: number;
  grandTotalPaise: number;
  /** An ISO INSTANT, not a calendar day. */
  cancelledAt: string | null;
  cancelReason: string | null;
  /** False once RETURNED, INVOICED or CANCELLED — the movement is over. */
  isEditable: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * `GET /challans` — envelope key `data`, like quotations and customers, and
 * UNLIKE `/invoices` which answers `invoices`. The API is not uniform and this
 * file does not pretend it is.
 *
 * `total` is the count matching the FILTER, not the count in the book — which is
 * what makes "Showing 1–50 of 214" true while `openOnly` is on.
 */
export interface ChallanListResponse {
  data: ChallanListRow[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * One challan, lines and all — `GET /challans/:id` → `{ data }`.
 * Transcribed from `apps/api/src/services/documents.ts` — `ChallanResponse`.
 *
 * THERE IS A PDF ROUTE NOW — see {@link endpoints.challanPdf}. This note used to
 * say there was none, and it was true when written: the route landed in
 * `apps/api/src/routes/documents-pdf.ts` rather than in `routes/challans.ts`,
 * which is why nobody reading `routes/challans.ts` ever found it. It takes
 * `?copy=` and prints Rule 55(1)'s three captioned copies.
 */
export interface ChallanDetail {
  kind: "challan";
  id: number;
  /** `CHL/26-27/7`. Allocated under the company row lock, gap-free (§6). */
  number: string;
  /** `YYYY-MM-DD` calendar day. */
  date: string;
  customerId: number;
  /**
   * The buyer FROZEN onto the document the day the goods moved (§4).
   *
   * ON AN INWARD CHALLAN THIS IS NOT THE BUYER, it is the CONSIGNER — the
   * customer sent the material in. The field is "the other party"; read
   * {@link direction} before labelling it on screen.
   */
  customer: DocumentCustomerSnapshot | null;
  attnName: string | null;
  status: ChallanStatus;
  challanType: ChallanType;
  /** See {@link ChallanListRow.direction} — derived server-side, never stored. */
  direction: ChallanDirection;
  placeOfSupply: string;
  /** `INTRA_STATE` | `INTER_STATE` — from the two state codes, as the tax was. */
  supplyType: string;
  /** The lorry. Rule 55 does not require it; the gate does. Text, never a link. */
  vehicleNumber: string | null;
  /**
   * Where the goods physically went, when that is NOT the customer's registered
   * address — a galvaniser's workshop, a site.
   *
   * BLANK IS THE ORDINARY CASE and it means "the address printed above". Render
   * nothing rather than inventing a placeholder for it.
   */
  shipToAddress: string | null;
  isReturnable: boolean;
  /** `YYYY-MM-DD` calendar day, or null when nothing is expected back. */
  expectedReturnDate: string | null;
  /** An ISO INSTANT. */
  returnedAt: string | null;
  /** The one real foreign key on a challan — see {@link ChallanListRow}. */
  linkedInvoiceId: number | null;
  /** An ISO INSTANT. Both null unless `status` is CANCELLED. */
  cancelledAt: string | null;
  cancelReason: string | null;
  /**
   * Rule 138, THE SECONDARY CASE — see {@link InvoiceDetail.ewbNo} for the
   * whole argument, and {@link endpoints.challanEwb} for why a challan carries
   * these at all when the CA said the goods move on the TAX INVOICE.
   *
   * Transcribed here because the server sends them on every challan. Nothing on
   * the challan screens renders them yet; a transcription that omits a field
   * the wire carries drops it silently, which is the failure this file's header
   * warns about, so the type tells the truth ahead of the screen.
   */
  ewbNo: string | null;
  ewbDate: string | null;
  ewbValidUntil: string | null;
  /**
   * WHETHER A `PUT` WOULD BE ACCEPTED RIGHT NOW — the server's own answer.
   *
   * A challan IS editable while OPEN, unlike an issued invoice, and that is not
   * an inconsistency: an invoice is a tax document corrected by a credit note
   * (§17.3), whereas a challan records a physical movement and the paper has to
   * match what actually went on the lorry. It freezes the moment the movement
   * closes. This flag may HIDE an editor; it must never be the thing that
   * authorises one — that is `challan.edit` (§2.1).
   */
  isEditable: boolean;
  /**
   * The server's figures (§6). `amountInWords` is DERIVED here rather than
   * stored — `delivery_challans` has no such column, because the figure is a
   * declared value in transit and not a sum anyone pays.
   */
  totals: DocumentTotals;
  lines: DocumentLine[];
  notes: string | null;
  terms: string | null;
  createdAt: string;
  updatedAt: string;
}

/* ========================================================================== */
/* Credit notes — §17.3, section 34(1) CGST                                   */
/*                                                                            */
/* ═══════════════════════════════════════════════════════════════════════════ */
/* A CREDIT NOTE **IS** A TAX DOCUMENT, unlike the challans above it, and     */
/* that is the difference every screen has to respect. It reduces output tax  */
/* liability and is declared in the CDNR section of GSTR-1. The figures below */
/* are real tax, reversed at the ORIGINAL supply's rates.                     */
/*                                                                            */
/* NOTHING HERE IS NEGATIVE, AND NOTHING MAY BE RENDERED WITH A MINUS SIGN.   */
/* Amounts are stored and printed POSITIVE; the document TYPE carries the     */
/* meaning "this reduces". A screen that shows −₹2,340 is inventing a sign the */
/* database does not hold, and a sign is how a sum silently changes direction. */
/* The one exception is `totals.roundOffPaise`, which is signed on every       */
/* document in this system and is a rounding, not an amount.                  */
/* ═══════════════════════════════════════════════════════════════════════════ */
/* ========================================================================== */

/**
 * One row of the credit-note register.
 * Transcribed from `apps/api/src/services/credit-notes.ts` — `CreditNoteSummary`.
 *
 * IT NAMES THE ORIGINAL ON EVERY ROW — number and date — because a list of
 * credit notes that does not say what each one reduces cannot be read. §34's
 * link is not only a detail-screen field.
 */
export interface CreditNoteListRow {
  id: number;
  /** `CRN/26-27/1` — its own series, reset each financial year (§6). */
  number: string;
  /** `YYYY-MM-DD` — a calendar day in Asia/Kolkata, never an instant. */
  date: string;
  /** `ISSUED` | `CANCELLED`. There is no draft and no other progression. */
  status: CreditNoteStatus;
  /** §34's mandatory link — a real foreign key, and always a live invoice. */
  invoiceId: number;
  /** FROZEN at issue, so the note keeps naming what it reduced. */
  originalInvoiceNumber: string;
  /** `YYYY-MM-DD`. */
  originalInvoiceDate: string;
  customerId: number;
  /**
   * From the document's FROZEN snapshot (§4).
   *
   * NULL, NOT THE EMPTY STRING, when the snapshot is unreadable — the INVOICE
   * convention (`toCreditNoteSummary` writes `snapshot?.name ?? null`), not the
   * quotation/challan one. Test for null here; testing for `""` as the challan
   * register does would leave a blank cell.
   */
  customerName: string | null;
  /** COPIED from the original invoice, never re-derived from the buyer (§8.7). */
  supplyType: string;
  /** §34(1) — why the bill was reduced. Mandatory, and printed. */
  reason: string;
  /** INTEGER PAISE, POSITIVE — the amount this note credits back. */
  grandTotalPaise: number;
  totalTaxPaise: number;
  /** An ISO INSTANT, not a calendar day. */
  cancelledAt: string | null;
  cancelReason: string | null;
  createdAt: string;
  updatedAt: string;
}

/** `GET /credit-notes` — envelope key `data`, like quotations and challans. */
export interface CreditNoteListResponse {
  data: CreditNoteListRow[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * One credit note, lines and all — `GET /credit-notes/:id` → `{ data }`.
 * Transcribed from `apps/api/src/services/documents.ts` — `CreditNoteResponse`.
 */
export interface CreditNoteDetail {
  kind: "creditNote";
  id: number;
  /** `CRN/26-27/1`. Allocated under the company row lock, gap-free (§6). */
  number: string;
  /** `YYYY-MM-DD` — the note's OWN date, not the invoice's. */
  date: string;
  customerId: number;
  /**
   * The buyer FROZEN onto the note — COPIED OFF THE INVOICE, not read from the
   * customer row today (§8.7). A buyer who has moved office since is still
   * credited at the address they were billed at.
   */
  customer: DocumentCustomerSnapshot | null;
  attnName: string | null;
  status: CreditNoteStatus;
  /** §34's link — the FK, and the two frozen fields that PRINT beside it. */
  invoiceId: number;
  originalInvoiceNumber: string;
  originalInvoiceDate: string;
  placeOfSupply: string;
  /**
   * `INTRA_STATE` | `INTER_STATE`, COPIED FROM THE ORIGINAL INVOICE.
   *
   * Never re-derived from the customer's state today: a buyer who has moved
   * state since must not flip an intra-state bill's CGST+SGST into IGST on the
   * note, or the original liability stays un-reversed in two heads while a new
   * one is created in a third.
   */
  supplyType: string;
  /** §34(1) — mandatory. Shown on the screen and printed on the sheet. */
  reason: string;
  /** An ISO INSTANT. Both null unless `status` is CANCELLED. */
  cancelledAt: string | null;
  cancelReason: string | null;
  /**
   * ALWAYS FALSE, and present anyway.
   *
   * A credit note is never edited — there is no `creditnote.edit` capability for
   * anyone (§2.1), for the same reason there is no `invoice.edit`. The field is
   * carried so the shared document screens can ask one question of all four
   * kinds rather than special-casing this one, which is how a fourth type
   * becomes a fourth branch.
   */
  isEditable: boolean;
  /** The server's figures (§6). Every amount POSITIVE. */
  totals: DocumentTotals;
  lines: DocumentLine[];
  notes: string | null;
  /**
   * ALWAYS `null` — `credit_notes` has no `terms` column, deliberately. Standing
   * terms are an offer about future work, and a document reducing a bill already
   * issued has nothing to offer.
   */
  terms: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Every `GET /…/:id` answers `{ data }`. */
export interface DocumentEnvelope<T> {
  data: T;
}

/**
 * One row of the invoice register (§11.2's "all bills", §17.4).
 * Transcribed from `apps/api/src/routes/invoices.ts` — `InvoiceListRow`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE FIELD IS `number`, NOT `invoiceNumber`. `toInvoiceListRow()` renames it on
 * the way out — the Prisma column is `invoiceNumber`, the wire field is `number`.
 * Reading `row.invoiceNumber` here compiles to `undefined` and renders a blank
 * cell in the one column the whole register is FOR.
 *
 * Likewise `date`, not `invoiceDate`. It is a CALENDAR DAY (`YYYY-MM-DD`), never
 * an instant — format it with `useFormatDay`, never `new Date(...)`, which parses
 * it as UTC midnight and shows the previous day west of Greenwich.
 *
 * NO LINES ON IT. The register does not carry line items; opening a document
 * fetches them. A hundred rows each with their lines is what makes a register
 * slow on a workshop connection.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export interface InvoiceListRow {
  id: number;
  /** `26-27/67` (§6). The wire name is `number`. */
  number: string;
  /** `YYYY-MM-DD` — a calendar day in Asia/Kolkata. */
  date: string;
  customerId: number;
  /** From the document's FROZEN snapshot — who was billed that day (§4). */
  customerName: string | null;
  /** `DRAFT` | `PENDING_IRN` | `ISSUED` | `CANCELLED` — see prisma/schema.prisma. */
  status: string;
  supplyType: string;
  placeOfSupply: string;
  soNumber: string | null;
  /** §6.1 — this one came out of the hand-written book. */
  issuedManually: boolean;
  /** INTEGER PAISE. Format through `formatRupees`. */
  subtotalPaise: number;
  totalTaxPaise: number;
  grandTotalPaise: number;
  /** An ISO INSTANT (not a calendar day) — or null when the bill stands. */
  cancelledAt: string | null;
}

/**
 * `GET /invoices` — note the envelope key is `invoices`, not `data`.
 *
 * `total` is the count matching the FILTER, not the count in the book, which is
 * what makes "Showing 1–50 of 214" true while a status filter is on.
 */
export interface InvoiceListResponse {
  invoices: InvoiceListRow[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * The home screen payload (§11.2).
 * Transcribed from `apps/api/src/routes/dashboard.ts` — `DashboardSummaryResponse`.
 */
export interface DashboardDocumentCard {
  /**
   * THE SHARED UNION, NOT A HAND-TYPED PAIR — and this line is why the fix to
   * the recents card was four files rather than one.
   *
   * It read `"invoice" | "quotation"` (transcribed from an API that said the
   * same) until 2026-08-16. Widening the API alone would have left this
   * transcription lying, and `RecentDocuments.tsx` picked its destination with
   * `doc.kind === "invoice" ? routes.invoice : routes.quotation` — so a challan
   * card would have opened the QUOTATION of that id. Widening THIS is what turns
   * that ternary into a compile error. Do the type first, always.
   */
  kind: DocumentKind;
  id: number;
  /** `26-27/67` for an invoice, `542/26` for a quotation, `CHL/26-27/7` (§6). */
  number: string;
  /** `YYYY-MM-DD` — a calendar day in Asia/Kolkata, not an instant. */
  date: string;
  status: string;
  customerId: number;
  /** The name AS PRINTED, from the document's frozen snapshot. Null if unreadable. */
  customerName: string | null;
  /**
   * INTEGER PAISE. Format with `formatPaise` — never `toLocaleString`.
   *
   * WHAT IT IS A FIGURE *OF* DEPENDS ON {@link kind}, and on a CHALLAN it is not
   * money anyone owes: Rule 55(1)'s declared value of the goods in transit
   * (§17.7). Render it through `DOCUMENT_AMOUNT_STYLE` in
   * `features/documents/document-kind.tsx`, which is where that judgement is
   * written down once — not in the same weight as a bill's total.
   */
  amountPaise: number;
  /** True when `amountPaise` is PRE-TAX because the quotation says "tax extra" (§9.3). */
  taxExtra: boolean;
}

/**
 * An inclusive span of calendar days, as `/dashboard/summary` answers it.
 *
 * BOTH ENDS INCLUSIVE, and that is the whole point: `toPeriod()` in
 * apps/api/src/routes/dashboard.ts converts its half-open internal window into
 * this pair precisely so the client can hand `from` and `to` straight to
 * `GET /invoices?from=&to=` when the owner taps the figure (§11.2 — "every
 * figure is tappable and leads somewhere useful"). The exclusive `until` never
 * crosses the wire; an earlier transcription of this interface claimed it did,
 * which would have made the month link miss the last day of every month.
 */
export interface DashboardPeriod {
  /** `YYYY-MM-DD`, the first day of the window. */
  from: string;
  /** `YYYY-MM-DD`, the LAST day of the window — inclusive. */
  to: string;
}

export interface DashboardSummaryResponse {
  financialYear: DashboardPeriod & {
    label: string;
    fyShort: string;
    startYear: number;
  };
  month: DashboardPeriod & {
    isCurrentMonth: boolean;
    invoiceCount: number;
    /**
     * INTEGER PAISE, and BILLED — not received. §11.2's "known gap": nothing on
     * the home screen may imply payment status until §17.5 exists. Do not label
     * this "collected", "earned" or "revenue" (§11.2: plain language, and
     * "Bills this month", not "Revenue MTD").
     */
    totalBilledPaise: number;
  };
  /**
   * The chart's twelve columns, April → March, ALWAYS all twelve — empty months
   * and months that have not happened yet included. Do not filter the zeros out
   * before plotting: a line drawn straight from April to June claims business in
   * a May that had none.
   *
   * BILLED, not received, exactly as `month.totalBilledPaise` above. §11.2's
   * amendment grants this one chart on the condition that it is labelled
   * "billed" and that every column opens that month's bills — the same gesture
   * the at-a-glance figures offer, extended to the other eleven months.
   */
  monthlyBilled: (DashboardPeriod & {
    invoiceCount: number;
    /** INTEGER PAISE. Format with `formatRupees`. */
    totalBilledPaise: number;
  })[];
  quotationsAwaitingReply: {
    count: number;
    /** The status the tile's destination filters by — always `SENT`. */
    status: string;
  };
  /**
   * §17.7's payoff on the first screen — "Delivered, not yet billed: 3".
   *
   * ───────────────────────────────────────────────────────────────────────────
   * OPTIONAL, AND ITS ABSENCE IS MEANINGFUL. `GET /dashboard/summary` is
   * deliberately NOT behind a feature flag while every challan route is behind
   * `requireFlag("challans")`, so the summary OMITS this key entirely when the
   * module is switched off rather than reporting 0 — a zero still announces
   * that the feature exists (§18.3). The card reads `undefined` as "not
   * available" and renders nothing, which is the same thing it does at zero.
   *
   * A COUNT AND NOTHING ELSE. There is no rupee figure here on purpose: §11.2
   * forbids the home screen implying payment status, and §17.7 says a challan's
   * value "is not money anyone owes" — a total of unbilled deliveries reads as
   * a debtors figure, which is precisely the claim this app cannot make until
   * §17.5 exists.
   *
   * The count is the `openOnly` predicate — OPEN, unlinked, and of a BILLABLE
   * type — which is the same predicate behind `routes.challansUnbilled`, so the
   * number and the list it opens cannot disagree.
   * ───────────────────────────────────────────────────────────────────────────
   */
  unbilledDeliveries?: {
    count: number;
  };
  /**
   * §17.5's payoff on the first screen — "Unpaid bills: 7 — ₹3,42,150", and the
   * figure §11.2 called the single most useful one a business owner can have.
   *
   * ───────────────────────────────────────────────────────────────────────────
   * OPTIONAL, ON THE SAME TWO GATES AS `unbilledDeliveries` — the `payments`
   * module on, and the caller holding `payment.view` — and ABSENT rather than
   * zeroed for the same §18.3 reason. Here the zero would be worse than a leak:
   * "Unpaid bills: 0" is a positive claim that every bill has been settled, made
   * by a build that is not recording payments at all.
   *
   * IT IS ABSENT AT A GENUINE ZERO TOO. The line is a to-do; one that is
   * permanently on screen saying "nothing to chase" is the thing the eye learns
   * to skip, so the day it says 7 it would be skipped as well.
   *
   * A COUNT *AND* A RUPEE FIGURE, unlike §17.7's count — that is the whole point
   * of §17.5 landing. "Unpaid bills: 7" without the amount is the question
   * without the answer. Both come from `readOutstandingInvoices`, the SAME
   * producer behind `GET /payments/outstanding`, so this total and the list the
   * card opens are the same arithmetic over the same rows.
   *
   * IT IS NOT "OVERDUE" AND MUST NEVER BE LABELLED SO. This system models no
   * credit terms and stores no due date; calling a 40-day-old bill overdue would
   * be the app inventing an agreement the business never made.
   * ───────────────────────────────────────────────────────────────────────────
   */
  unpaidBills?: {
    count: number;
    /** INTEGER PAISE. Format with `formatRupees` — never `toLocaleString`. */
    totalPaise: number;
  };
  /** Null before the first invoice exists; the "Repeat Last Bill" tile hides itself. */
  lastBill: DashboardDocumentCard | null;
  recentDocuments: DashboardDocumentCard[];
  /** When the snapshot was taken — so a cached screen can say it is cached. */
  generatedAt: string;
}

/* ========================================================================== */
/* Payments — §17.5                                                           */
/*                                                                            */
/* ═══════════════════════════════════════════════════════════════════════════ */
/* THESE ARE RE-EXPORTS, NOT TRANSCRIPTIONS, AND THAT IS THE DIFFERENCE FROM  */
/* EVERY OTHER SHAPE IN THIS FILE.                                            */
/*                                                                            */
/* `InvoiceListRow` and friends above are transcribed because their originals */
/* live inside an API route file that the SPA cannot import. The payment       */
/* shapes do not: `PaymentResponse`, `InvoicePaymentSummary`,                 */
/* `OutstandingInvoiceRow`, `CustomerOutstanding` and the three envelopes are */
/* declared in `packages/types/src/documents.ts` — the same package the server */
/* validates and answers with. Copying them here would create a second        */
/* definition of "how much is still owed", which is precisely the thing §17.5  */
/* spends its opening paragraphs refusing to allow anywhere in this system.    */
/*                                                                            */
/* They are re-exported rather than imported directly by each screen so that   */
/* a screen author still finds every payment shape where they find every other */
/* response shape, which is what this file is for.                            */
/*                                                                            */
/* MONEY IS A PLAIN JSON NUMBER OF INTEGER PAISE on every `…Paise` key, as     */
/* everywhere else on this wire. Format through `formatPaise`/`formatRupees`  */
/* (lib/money), never `toLocaleString` (CLAUDE.md).                           */
/* ═══════════════════════════════════════════════════════════════════════════ */
/* ========================================================================== */

/* ========================================================================== */
/* Notifications — the hub                                                    */
/* ========================================================================== */

/**
 * One notification, as `GET /notifications` returns it.
 * Transcribed from `apps/api/src/routes/notifications.ts` — `NotificationRow`.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THERE IS NO PROSE ON THE WIRE, AND THAT IS THE DESIGN. The row carries an
 * `event` key and a `payload` of raw values — integer paise for money, calendar
 * days for dates — and the CLIENT turns those into a sentence through its own
 * i18n. So Marathi says it in Marathi (§11.4), and every rupee goes through
 * `formatRupees` exactly once, on the way to a screen (CLAUDE.md). A server that
 * composed the sentence would have to pick a language for a reader it cannot
 * see, and would format money in a runtime where `small-icu` silently emits
 * western grouping.
 *
 * `link` IS BUILT SERVER-SIDE, by `notificationLink()` in `@steelbill/types`, so
 * the row and its destination are decided in one place — and so a future email
 * digest (§17A) sends the same URL rather than inventing a second scheme. It is
 * NULLABLE: a row whose `entityType` the catalogue no longer knows still LISTS,
 * it just cannot be tapped. Dropping it would hide an alert somebody was sent;
 * inventing a URL for it would send them somewhere that does not exist.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export interface NotificationRow {
  id: number;
  /**
   * A catalogue key — `invoice.eway_bill.required` and the rest. The typed union
   * is `NotificationEvent` in `@steelbill/types`; this is `string` on the wire
   * because a row written by a NEWER server than this bundle must still render
   * rather than crash the hub. Narrow it with `isNotificationEvent` at the point
   * of display, and show a plain fallback for anything unrecognised.
   */
  event: string;
  /** What the link OPENS — not necessarily what happened. See `link`. */
  entityType: string;
  entityId: number;
  /** An SPA path (`/invoices/67`), or null when nothing can be opened. */
  link: string | null;
  /** Raw render values. Money is integer paise; dates are calendar days. */
  payload: unknown;
  /** Who caused it. Never the reader — the actor is excluded server-side. */
  actorUserId: number | null;
  /** An ISO INSTANT, or null while it is unread. */
  readAt: string | null;
  /** An ISO INSTANT. Order comes from the server; do not re-sort these rows. */
  createdAt: string;
}

/** `GET /notifications` — envelope key `notifications`, plus `unread`. */
export interface NotificationListResponse {
  notifications: NotificationRow[];
  total: number;
  /** Unread across the WHOLE inbox, not within this page or this filter. */
  unread: number;
  limit: number;
  offset: number;
}

/** `GET /notifications/unread-count`. One integer, UNWRAPPED (no `data` key). */
export interface NotificationUnreadCountResponse {
  unread: number;
}

/* ========================================================================== */
/* Messages — the shared conversation                                         */
/*                                                                            */
/* RE-EXPORTS, NOT TRANSCRIPTIONS, unlike the notification shapes above. Those */
/* are declared inside an API route file the SPA cannot import; these live in  */
/* `packages/types/src/messages.ts` — the same file the SERVER validates and   */
/* answers with, and the same file that owns `MESSAGE_BODY_MAX` and the        */
/* attachment → capability map. A second copy here would be a second answer to */
/* "how long may a message be", and the two would eventually differ by one     */
/* character in a form that then 400s on a body the client thought was fine.   */
/*                                                                            */
/* They are re-exported rather than imported directly by each screen so that a */
/* screen author still finds every response shape where they find every other  */
/* one, which is what this file is for.                                        */
/* ========================================================================== */

export type {
  MessageAttachmentInput,
  MessageAttachmentResponse,
  MessageAttachmentType,
  MessageListResponse,
  MessageReadResponse,
  MessageResponse,
  MessageSendInput,
  MessageSendResponse,
  MessageUnreadCountResponse,
} from "@steelbill/types";

/*
 * The private half of the same messenger. RE-EXPORTS for the reason above —
 * these are the very interfaces `apps/api/src/routes/direct-messages.ts`
 * answers with, so a field renamed on the server is a typecheck failure here
 * rather than an `undefined` on a screen.
 */
export type {
  DirectConversationListResponse,
  DirectConversationSummary,
  DirectMessageListResponse,
  DirectMessageReadResponse,
  DirectMessageSendResponse,
} from "@steelbill/types";

export { MESSAGE_BODY_MAX, MESSAGE_ATTACHMENT_TYPES } from "@steelbill/types";

export type {
  CustomerOutstanding,
  CustomerStatementResponse,
  InvoicePaymentSummary,
  InvoicePaymentsResponse,
  OutstandingInvoiceRow,
  OutstandingResponse,
  PaymentMethod,
  PaymentResponse,
  PaymentState,
} from "@steelbill/types";

export { PAYMENT_METHODS, PAYMENT_STATES } from "@steelbill/types";

/* ========================================================================== */
/* Reports and the CA export — §11.2, §17.4                                   */
/*                                                                            */
/* RE-EXPORTS, NOT TRANSCRIPTIONS, for the same reason the payment shapes     */
/* above are: `packages/types/src/reports.ts` is the file the SERVER answers   */
/* from, and it carries — in prose, at length — the rule about which document  */
/* type reaches which column. A second copy here would be a second place for   */
/* "does a challan count toward billed?" to be answered, and the two answers   */
/* would eventually differ. Read that file before touching any figure on the   */
/* Reports screen; it is where the four-document rules are written down.       */
/* ========================================================================== */

export type {
  Gstr1Section,
  OutstandingAgeBucket,
  OutstandingAgeBucketKey,
  OutstandingAgeingResponse,
  ReportCustomerRow,
  ReportCustomersResponse,
  ReportDiscounts,
  ReportFinancialYear,
  ReportNetTotals,
  ReportPeriod,
  ReportPeriodPreset,
  ReportSummaryResponse,
  ReportTotals,
} from "@steelbill/types";

export {
  GSTR1_SECTIONS,
  OUTSTANDING_AGE_BUCKETS,
  REPORT_PERIOD_PRESETS,
} from "@steelbill/types";
